import React, { useState, useEffect, useRef } from 'react';
import {
  Printer,
  Tags,
  Settings,
  Package2,
  PenTool,
    Plus,
    ScanBarcode,
    MapPin
} from 'lucide-react';
import { PalletSummaryDto, CreateOrderSSCCRequest } from '../../../../types/picking';
import { pickingService, PackageDto } from '../../../../services/pickingService';
import { dataInspectionService } from '../../../../services/dataInspectionService';
import { ApiError } from '../../../../utils/apiError';
import { mandatoryCountingService } from '../../../../services/mandatoryCountingService';
import { BackButton } from '../../../common/BackButton';
import { MediaCaptureButton } from '../../../common/media/MediaCaptureButton';
import { GS1Scanner, GS1ScannerRef, GS1ScanData } from './GS1Scanner';
import { PalletManager } from './PalletManager';
import { ActiveLine } from './ActiveLine';
import { PickingLinesList } from './PickingLinesList';
import { ImagePanel } from './ImagePanel';
import { StatusBar } from './StatusBar';
import { OrderListBadge, splitOrderNoERPs } from './OrderListBadge';
import {
  LocationScanModal,
  LocationOverrideModal,
  LocationStockModal,
  ImageModal,
  SplitModal,
  QuantityDistributionModal,
  PalletInfoModal,
  DeliveryInfoModal,
  QuantityConfirmModal,
  QuantityDeviationModal,
  SignatureModal,
  SettingsModal,
  PickingPrintModal,
  MandatoryCountingModal,
  ZeroPointCountingModal,
  PackageScanModal,
  SerialNumberModal,
  SerialPickModal,
  BatchPickModal,
  BatchNumberModal,
  AddLineModal,
  PackZoneSelectionModal,
  CarrierGateDialog,
  TaValidationDialog,
  CarrierDistributionModal,
  PickCompletionWarningModal
} from './modals';
import { GS1Data } from './types';
import { applyStructureCaps } from './structureCaps';
import { usePalletManagement } from '../../../../hooks/usePalletManagement';
import { isEmptyPallet, isDatabasePallet } from '../../../../types/picking';
import { useSimpleBarcodeScanner } from '../../../../hooks/useSimpleBarcodeScanner';
import { useBarcodeScannerEnabled } from '../../../../hooks/useBarcodeScannerEnabled';
import { useShowSendToPacking } from '../../../../hooks/useShowSendToPacking';
import { useAutoCreatePalletOnDPakScan } from '../../../../hooks/useAutoCreatePalletOnDPakScan';
import { useScanFeedback } from '../../../../hooks/useScanFeedback';
import { transitPackagingService } from '../../../../services/transitPackagingService';
import { TransitPackagingDto } from '../../../../types/shipmentLine';
import { useDPakScan } from './hooks/useDPakScan';
import { countRegisteredBatches, getPalletAllocationsForLine, getCarrierDisplayName, getSiblingLines, isPalletRowForLine, palletRowLineNo } from './palletHelpers';
import type { CarrierDistributionResult, ExistingDistributionCarrier } from './modals/CarrierDistributionModal';
import { useAutoCreateAndPrintKolliOnPickStart } from '../../../../hooks/useAutoCreateAndPrintKolliOnPickStart';
import { usePickingLargeText } from '../../../../hooks/usePickingLargeText';
import { NumericInput } from '../../../common/inputs';
import { MobileBarcodeCaptureInput } from '../../../common/scanning';
import { parseGS1Barcode as parseGS1BarcodeFull } from '../../../../utils/gs1Parser';
import { printingService } from '../../../../services/printingService';
import { SSCCLabelType } from '../../../../types/printing';
import { useAuth } from '../../../../contexts/AuthContext';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { usePickingData, ExtendedPickingLine, isOverPickAllowed } from './hooks/usePickingData';
import { usePickingCompletion } from './hooks/usePickingCompletion';
import { usePickingSplit } from './hooks/usePickingSplit';
import { useSsccReports } from './hooks/useSsccReports';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { itemsService } from '../../../../services/itemsService';

interface ModernPickingDetailsProps {
  pListId: number;
  onBack: () => void;
}

export const ModernPickingDetails: React.FC<ModernPickingDetailsProps> = ({
  pListId,
  onBack
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentWarehouse, warehouses } = useWarehouse();
  const isAdvancedWarehouse = warehouses.find(w => w.warehouseID === currentWarehouse)?.useAWYesNo ?? false;

  // Refs
  const scannerRef = useRef<GS1ScannerRef>(null);

  // ─── Pallet management hook ───
  const {
    pallets,
    activePalletSSCC,
    activePallet,
    emptyPallets,
    databasePallets,
    setActivePalletSSCC,
    loadPallets,
    createEmptyPallet,
    setEmptyPalletCarrier,
    deleteEmptyPallet
  } = usePalletManagement(pListId);

  // Convert UnifiedPallet[] to PalletSummaryDto[] for components
  const palletSummaries: PalletSummaryDto[] = pallets.map(p => {
    if (isEmptyPallet(p)) {
      return {
        sscc: p.sscc,
        ssccLevel: p.ssccLevel,
        totalItems: 0,
        totalQuantity: 0,
        totalWeight: 0,
        isActive: p.sscc === activePalletSSCC,
        created: p.created,
        createdBy: p.createdBy,
        transitPackagingID: p.transitPackagingID ?? null,
        transitPackagingDescr: p.transitPackagingDescr ?? null,
        items: []
      };
    } else {
      return {
        ...p,
        isActive: p.sscc === activePalletSSCC
      };
    }
  });

  // Convert activePallet to PalletSummaryDto format
  const activePalletSummary: PalletSummaryDto | undefined = activePallet
    ? palletSummaries.find(p => p.sscc === activePallet.sscc)
    : undefined;

  // D-pak-skann → automatisk pall. Default av; når den er av oppfører alt seg nøyaktig
  // som før. Leses her (før datahooken) fordi den styrer hvordan paller matches mot
  // linjer og hvordan batch telles. Se «Claude md files/D-Pak-Pall-Nivaa3/Plan.md».
  const autoCreatePalletOnDPakScan = useAutoCreatePalletOnDPakScan();

  // ─── Data hook (loading, settings, helpers) ───
  const pickingData = usePickingData(pListId, palletSummaries, autoCreatePalletOnDPakScan);
  const {
    pickingDetails, pickingLines, setPickingLines, loading, error,
    userProfile, pListOrders, setPListOrders,
    activeLineIndex, setActiveLineIndex, currentLineIndex, setCurrentLineIndex,
    lineStartTimes, setLineStartTimes,
    activeLine, currentLine, completedLines,
    enableRapidScanning, setEnableRapidScanning,
    autoShowImage, setAutoShowImage,
    enableDragAndDrop, setEnableDragAndDrop,
    zeroPointCountingEnabled, fewPointCountingThreshold,
    loadPickingDetails, loadPListOrders, loadUserProfile,
    reorganizeLines, findLineByItem, convertToPickingLine, convertToPickingLineWithCheck,
  } = pickingData;

  // ─── Lydfeedback ved skanning (Haucon 36334085) — styrt av picking.scanSoundEnabled ───
  const { playSuccess: playScanSuccess, playError: playScanError } = useScanFeedback();

  // ─── Skann vs. manuell (36334078): kanalen inndata kom fra, satt av skann-handlerne ───
  // Leses og nullstilles av handleUpdateQuantity. Null ved bekreftelse = manuell vei
  // (Plukk-knapp, antall-modal uten forutgående skann).
  const pendingEntryMethodRef = useRef<{ method: 'Scanned' | 'Manual'; barcode?: string } | null>(null);

  // ─── Lastbærer-valg ved plukk mot pall (36334224) ───
  // Bærerne lastes én gang; valgt bærer settes på nye OrderSSCC-rader (pall-nivå via
  // MIN() i summary). Default = TransitPackaging.DefaultYesNo. Null = ikke valgt (som før).
  const [palletCarriers, setPalletCarriers] = useState<TransitPackagingDto[]>([]);
  const [palletCarrierId, setPalletCarrierId] = useState<number | null>(null);
  // Fordel uplasserte varer under plukk (TW 36396316)
  const [showPickDistribution, setShowPickDistribution] = useState(false);
  const [pickDistributionBusy, setPickDistributionBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const carriers = await transitPackagingService.getAll();
        if (cancelled) return;
        setPalletCarriers(carriers);
        const defaultCarrier = carriers.find(c => c.defaultYesNo);
        if (defaultCarrier) setPalletCarrierId(defaultCarrier.transitPackagingID);
      } catch (err) {
        console.warn('Could not load transit packagings for pallet carrier select:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /**
   * Lastbærertype for rader som legges på en eksisterende pall: pallens egen type først
   * (kan være byttet, TW 36396318), deretter valget for nye paller. Uten dette fikk nye
   * rader nedtrekkets type, og pallen endte med blandede typer.
   */
  const carrierIdForPallet = (sscc: string): number | undefined =>
    palletSummaries.find(p => p.sscc === sscc)?.transitPackagingID ?? palletCarrierId ?? undefined;

  // ─── Completion hook (complete picking, send to packing, delivery info) ───
  const completion = usePickingCompletion({
    pListId, pickingDetails, pickingLines, pListOrders, setPListOrders,
    emptyPallets, loadPickingDetails, onBack, carriers: palletCarriers, loadPallets,
  });
  const {
    showDeliveryInfoModal, setShowDeliveryInfoModal,
    showPackZoneSelection, setShowPackZoneSelection,
    showQuantityDeviation, quantityDeviations,
    showSignatureModal, setShowSignatureModal,
    handleCompletePicking, handleSendToPacking, handlePackZoneConfirm,
    handleDeliveryInfoConfirm, handleQuantityDeviationConfirm, handleQuantityDeviationCancel,
    handleSignatureConfirm,
    // Leveringsmåte-varsel ved avslutt plukk (Strømmes)
    showPickCompletionWarning, pickCompletionWarnings,
    handlePickCompletionWarningConfirm, handlePickCompletionWarningCancel,
    // Lastbærer-gate + fordelingsbilde (36334239)
    showCarrierGate, showCarrierDistribution, carrierBusy, deliveryInfoKey,
    handleCompleteWithoutCarrier, handleOpenCarrierDistribution,
    handleCarrierDistributionConfirm, handleCarrierGateCancel, handleCarrierDistributionCancel,
    // TA-forhåndsvalidering før fullføring (36369121)
    showTaValidation, taValidationIssues, taValidationBusy, taValidationError,
    handleTaValidationCancel, handleTaValidationProceed,
  } = completion;

  // ─── Split/Distribution hook ───
  const split = usePickingSplit({
    pListId, pickingLines, setPickingLines, setActiveLineIndex,
    palletSummaries, lineStartTimes, loadPallets, reorganizeLines,
    dpakMode: autoCreatePalletOnDPakScan,
    carrierId: palletCarrierId,
  });
  const {
    showSplitModal, splitLineNo, splitQuantities, setSplitQuantities,
    showQuantityDistribution, distributionLineNo, distributionQuantities, setDistributionQuantities,
    handleSplitLine, confirmSplit, closeSplitModal,
    confirmQuantityDistribution, closeDistributionModal,
  } = split;

  // ─── Remaining local UI state (tightly coupled to picking flow) ───
  const [showPalletManager, setShowPalletManager] = useState(false);
  const [printingActivePalletSSCC, setPrintingActivePalletSSCC] = useState(false);

  // SSCC-/pall-etikett-rapporter (ReportType 50) — gjør at utskrift sender `reportId`
  // i stedet for at backend hardkoder ReportID 50 (Del B i Plan-SSCC-Print-Plukk).
  const { reports: ssccReports, selectedReportId: selectedSsccReportId, setSelectedReportId: setSelectedSsccReportId } = useSsccReports();

  // PLOCK-2 KRAV 1: Auto-opprett kolli + skriv ut etikett ved plukk-start.
  // Gated bak profilinnstillingen 'picking.autoCreateAndPrintKolliOnPickStart' (default av).
  // Engangs-flagg slik at vi ikke triggrer ny opprettelse på hver re-render.
  const autoKolliEnabled = useAutoCreateAndPrintKolliOnPickStart();
  // picking.largeText (TW 36393697): større skrift/trykkmål på aktiv linje, linjelista og toppikonene.
  const largeText = usePickingLargeText();
  const headerBtnSize = largeText
    ? 'min-w-[52px] w-[52px] h-[52px]'
    : 'min-w-[32px] w-8 h-8 sm:min-w-[44px] sm:w-11 sm:h-11';
  // Punkt 2: dialog for antall internkolli ved plukkstart (vises hver gang plukket åpnes når settingen er på).
  const [kolliDialogHandled, setKolliDialogHandled] = useState(false);
  const [showKolliDialog, setShowKolliDialog] = useState(false);
  const [kolliCount, setKolliCount] = useState('1');
  // Header-ikon (Tags): modal for å velge antall kolli-etiketter (default 1) før utskrift.
  const [showPrintKolliModal, setShowPrintKolliModal] = useState(false);
  const [printKolliCount, setPrintKolliCount] = useState('1');

  // Image state
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [showImagePanel, setShowImagePanel] = useState(false);

  // Settings/Print modals
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showPickingPrintModal, setShowPickingPrintModal] = useState(false);

  // Location scanning
  const [showLocationScan, setShowLocationScan] = useState(false);
  const [locationScan, setLocationScan] = useState('');
  const [showLocationOverride, setShowLocationOverride] = useState(false);
  const [overrideLocation, setOverrideLocation] = useState('');
  // Lokasjonsoversikt-modal + saldo for gjeldende plukklokasjon (antall/reservert, vist inline)
  const [showLocationStock, setShowLocationStock] = useState(false);
  const [activeLineStock, setActiveLineStock] = useState<{ quantity: number; reserved: number } | null>(null);
  const [tempGS1Quantity, setTempGS1Quantity] = useState<number | null>(null);

  // Store pending GS1 scan data for use after location/quantity validation
  const [pendingGS1Data, setPendingGS1Data] = useState<GS1ScanData | null>(null);

  // Editing states
  const [editingLocation, setEditingLocation] = useState<number | null>(null);
  const [newLocation, setNewLocation] = useState('');
  const [editingQuantity, setEditingQuantity] = useState<number | null>(null);
  const [newQuantity, setNewQuantity] = useState('');

  // Pallet info
  const [showPalletInfo, setShowPalletInfo] = useState<string | null>(null);

  // Quantity confirmation modal
  const [showQuantityConfirm, setShowQuantityConfirm] = useState(false);
  const [quantityToConfirm, setQuantityToConfirm] = useState<number>(0);
  const [lineToConfirm, setLineToConfirm] = useState<ExtendedPickingLine | null>(null);
  // 'add' = skann-flyt (bekreftet antall legges til), 'setTotal' = redigering (bekreftet antall er ny total)
  const [quantityModalMode, setQuantityModalMode] = useState<'add' | 'setTotal'>('add');

  // Package scan modal (for batch picking)
  const [showPackageScan, setShowPackageScan] = useState(false);
  // Samplukk: eskene på plukklisten (PListOrderPackages) med ordrene hver eske tilhører.
  // Gir gyldige eske-ID-er i skanningen og ordregruppen for suppleringsesker (TW 36438546).
  const [batchPackages, setBatchPackages] = useState<PackageDto[]>([]);

  // Mandatory counting modal
  const [showMandatoryCounting, setShowMandatoryCounting] = useState(false);
  const [mandatoryCountingData, setMandatoryCountingData] = useState<{
    itemNo: string;
    itemDescription: string;
    binCode: string;
    lastCountedDate?: string | null;
    daysSinceLastCounted?: number | null;
    currentQuantity: number;
  } | null>(null);
  const [isSubmittingCounting, setIsSubmittingCounting] = useState(false);
  // Plukk-konteksten som skal gjenopptas etter at obligatorisk telling er registrert.
  // Settes av den sentrale telle-gaten i handlePickLine slik at vi kan fortsette
  // nøyaktig samme plukk (inkl. overstyrte linjer) uavhengig av currentLine-state.
  const [pendingPickAfterCount, setPendingPickAfterCount] = useState<{
    lineNo: number;
    qty: number;
    line: ExtendedPickingLine;
  } | null>(null);

  // Resttelling (compass.MandatoryCountingPickFirst): telle-plikten er utløst, men modalen
  // vises først ETTER at operatøren har bekreftet plukkantall. «Armeres» av telle-gaten i
  // checkAndPromptMandatoryCounting og konsumeres av handleQuantityConfirm-interceptet.
  // VIKTIG for invarianten: tellingen registreres fortsatt FØR plukket registreres
  // (talt totalsaldo = rest + plukket antall via samme submit-endepunkt), slik at
  // ItemBins/StcTransaction(TT100)/eksportfil/eSENTRAL blir identiske med tell-før-plukk.
  const [armedCountBack, setArmedCountBack] = useState<{
    pListLineNo: number;
    itemNo: string;
    itemDescription: string;
    binCode: string;
    warehouseID: string;
    lastCountedDate?: string | null;
    daysSinceLastCounted?: number | null;
    currentQuantity: number;
  } | null>(null);
  // Rest-modalen er åpen; plukkantallet operatøren bekreftet holdes til tellingen er sendt.
  const [restCountPending, setRestCountPending] = useState<{ confirmedQuantity: number } | null>(null);

  // Strukturplukk: frivillig telle-snarvei ved «maks mulig sett»-avvik.
  // Operatøren står på lokasjonen og teller inn faktisk antall — dekningen
  // rekalkuleres, og manko-varslene forsvinner hvis det er nok til alle sett.
  const [structureCountData, setStructureCountData] = useState<{
    pListLineNo: number;
    itemNo: string;
    itemDescription: string;
    binCode: string;
    warehouseID: string;
    currentQuantity: number;
  } | null>(null);
  const [isSubmittingStructureCount, setIsSubmittingStructureCount] = useState(false);

  // Zero-point counting modal
  const [showZeroPointCounting, setShowZeroPointCounting] = useState(false);
  const [zeroPointCountingData, setZeroPointCountingData] = useState<{
    itemNo: string;
    itemDescription: string;
    binCode: string;
    pickedQuantity: number;
    expectedRemaining: number;
    warehouse: string;
    pListLineNo: number;
  } | null>(null);
  const [isSubmittingZeroPoint, setIsSubmittingZeroPoint] = useState(false);

  // Serial number registration modal (ItemType=2 - free-text entry for documentation)
  const [showSerialNumberModal, setShowSerialNumberModal] = useState(false);
  const [serialNumberLine, setSerialNumberLine] = useState<ExtendedPickingLine | null>(null);

  // Serial pick modal (ItemType=6 - select from available serials in inventory)
  const [showSerialPickModal, setShowSerialPickModal] = useState(false);
  const [serialPickLine, setSerialPickLine] = useState<ExtendedPickingLine | null>(null);

  // Batch pick modal (ItemType=4/5 - select batch from available batches in inventory)
  const [showBatchPickModal, setShowBatchPickModal] = useState(false);
  const [batchPickLine, setBatchPickLine] = useState<ExtendedPickingLine | null>(null);

  // Batch number registration modal
  const [showBatchNumberModal, setShowBatchNumberModal] = useState(false);
  const [batchNumberLine, setBatchNumberLine] = useState<ExtendedPickingLine | null>(null);

  // Add additional line modal
  const [showAddLineModal, setShowAddLineModal] = useState(false);
  // TW 36393701: dialog for nytt antall på tilleggslinje { lineNo, qty (tekst) }
  const [editAdditionalLine, setEditAdditionalLine] = useState<{ lineNo: number; qty: string } | null>(null);


  // Location-scan-only mode: when only requireLocationScan is enabled (not requireItemScan)
  // In this mode, barcode scans are treated as location codes instead of item codes
  const isLocationScanOnlyMode = activeLine?.requireLocationScan === true
    && activeLine?.requireItemScan === false
    && activeLine?.binCode !== '[X]';

  // Global barcode scanner - works without input focus
  // Automatically disabled when modals are open or when setting is off
  const barcodeScannerEnabled = useBarcodeScannerEnabled();

  // Styrer om «Til pakking»-knappen vises (skjules for tenanter uten pakkemodul)
  const showSendToPacking = useShowSendToPacking();
  const isAnyModalOpen = showPalletManager || showImageModal || showSettingsModal ||
                         showPickingPrintModal || showLocationScan || showLocationOverride ||
                         showSplitModal || showQuantityDistribution || showDeliveryInfoModal ||
                         showQuantityConfirm || showQuantityDeviation || showSignatureModal ||
                         showMandatoryCounting || restCountPending !== null || showZeroPointCounting || showPackageScan ||
                         showSerialNumberModal || showSerialPickModal || showBatchPickModal || showBatchNumberModal || showAddLineModal ||
                         showPackZoneSelection || showLocationStock || showPrintKolliModal ||
                         showPickCompletionWarning;

  // Feil ved skann vises under skannefeltet i stedet for alert(): operatøren slipper å
  // trykke OK og kan skanne på nytt direkte. Med global skannerfangst trengs ikke fokus i
  // feltet, og fokus flyttes ikke dit (unngår at nettbrett-tastaturet spretter opp).
  const [scanError, setScanError] = useState<string | null>(null);
  const showScanError = (message: string) => {
    playScanError();
    setScanError(message);
    if (!barcodeScannerEnabled) {
      scannerRef.current?.focusInput();
    }
  };

  useSimpleBarcodeScanner({
    enabled: barcodeScannerEnabled && !isAnyModalOpen,
    onScan: (barcode: string) => {
      console.log('🌍 Global scan detected:', barcode);
      setScanError(null);

      // In location-scan-only mode, treat scan as location barcode
      if (isLocationScanOnlyMode && activeLine) {
        const cleanedBarcode = barcode.replace(/[~§]+$/g, '').trim();
        handleLocationOnlyScan(cleanedBarcode);
        return;
      }

      // Process the barcode using the same logic as GS1Scanner
      processGlobalBarcode(barcode);
    }
  });

  // Saldo (antall + reservert) for gjeldende plukklokasjon — vist inline på aktiv linje.
  // Henter ItemBins+StcReservation for varen og summerer radene for gjeldende bin (serie/batch
  // kan gi flere rader pr. bin). Hopper over vare-GO (stcTransactionsYesNo === false → ingen ItemBins).
  useEffect(() => {
    const itemNo = activeLine?.itemNo;
    const binCode = activeLine?.binCode;
    const warehouseId = activeLine?.warehouseID;
    const stockControlled = activeLine?.stcTransactionsYesNo !== false;
    if (!itemNo || !binCode || !stockControlled) {
      setActiveLineStock(null);
      return;
    }
    let cancelled = false;
    dataInspectionService.getItemBinsByItemNo(itemNo)
      .then(bins => {
        if (cancelled) return;
        const cur = binCode.toLowerCase();
        const matching = bins.filter(b =>
          (b.binCode || '').toLowerCase() === cur
          && (!warehouseId || !b.warehouseID || b.warehouseID === warehouseId));
        if (matching.length === 0) { setActiveLineStock(null); return; }
        setActiveLineStock({
          quantity: matching.reduce((s, b) => s + (b.quantity ?? 0), 0),
          reserved: matching.reduce((s, b) => s + (b.quantityReserved ?? 0), 0)
        });
      })
      .catch(err => {
        if (!cancelled) { console.warn('Kunne ikke laste saldo for aktiv plukklokasjon:', err); setActiveLineStock(null); }
      });
    return () => { cancelled = true; };
  }, [activeLine?.itemNo, activeLine?.binCode, activeLine?.warehouseID, activeLine?.stcTransactionsYesNo]);

  // Overstyr plukklokasjon fra lokasjonsoversikt-modalen. Gjenbruker den kanoniske
  // updateBinCode-flyten (samme som «vanlig» override) → identisk nedstrøms (PListLine.BinCode
  // + PListLineBinCodeChangedEvent → OrderLine.StdLocationName). Backend re-validerer bin+saldo.
  const handleLocationOverrideFromStock = async (newBinCode: string) => {
    if (!activeLine) return;
    try {
      await pickingService.updateBinCode(pListId, activeLine.pListLineNo, newBinCode);
      setPickingLines(prev => prev.map(l =>
        l.pListLineNo === activeLine.pListLineNo ? { ...l, location: newBinCode, binCode: newBinCode } : l
      ));
      setShowLocationStock(false);
      // activeLineStock oppdateres automatisk av effekten over når binCode endres.
    } catch (error) {
      console.error('Feil ved lokasjons-overstyring fra modal:', error);
      alert(t('warehouse.modules.picking.alerts.validateLocationFailed', {}, 'Kunne ikke endre lokasjon'));
    }
  };

  // Allokering (TW 36362298): ALC-{ResID}-etiketten (klistret på varene ved direktemottak)
  // bekrefter plukklinjen — den identifiserer reservasjonen entydig og erstatter dermed både
  // vare- og lokasjonsskann. Trigges KUN av ALC-prefikset og konsumerer skannet; alle andre
  // skann faller urørt gjennom til eksisterende løp, og plukklister uten allokerte linjer
  // er dermed helt uberørt.
  const tryHandleAllocationScan = (scanInput: string): boolean => {
    const cleaned = scanInput.replace(/[~§]+$/g, '').trim();
    const match = /^ALC-(\d+)$/i.exec(cleaned);
    if (!match) return false;

    const scannedResId = parseInt(match[1], 10);
    // Gjenstående antall (ikke isCompleted, som i plukk-UI-et betyr «har plukk»)
    const line = pickingLines.find(l => l.allocationResID === scannedResId && l.qtyPicked < l.qty);
    if (!line) {
      showScanError(t('warehouse.modules.picking.alerts.allocationLabelNoMatch', { 0: cleaned },
        `Allokeringsetiketten ${cleaned} hører ikke til noen gjenstående linje i denne plukklisten.`));
      return true; // konsumert — en ALC-kode skal aldri tolkes som vare-/lokasjonsskann
    }

    playScanSuccess();
    pendingEntryMethodRef.current = { method: 'Scanned', barcode: cleaned };
    const lineIndex = pickingLines.indexOf(line);
    setCurrentLineIndex(lineIndex);
    setActiveLineIndex(lineIndex);
    // Etiketten bekrefter både vare og lokasjon — hopp over begge skann-gatene.
    // handlePickLine tar fortsatt obligatorisk telling og antallsbekreftelse sentralt.
    handlePickLine(line.pListLineNo, line.qty);
    return true;
  };

  const processGlobalBarcode = async (scanInput: string) => {
    console.log('🔍 Processing global barcode:', scanInput);

    // Allokeringsetikett (ALC-{ResID}) prøves aller først — entydig prefiks, inert ellers.
    if (tryHandleAllocationScan(scanInput)) {
      return;
    }

    // D-pak-skann prøves FØRST. Returnerer den false (innstillingen er av, eller skannet
    // mangler D-pak-innhold) faller vi gjennom til uendret eksisterende logikk under.
    if (await dpak.tryHandleDPakScan(scanInput)) {
      return;
    }

    // First, remove trailing scanner separators to check for real GS1 structure
    const withoutTrailingSeparators = scanInput.replace(/[~§]+$/g, '').trim();

    // Check if this looks like a GS1-128 structured barcode
    const hasInternalSeparators = withoutTrailingSeparators.includes('~') ||
                                   withoutTrailingSeparators.includes('§') ||
                                   withoutTrailingSeparators.includes(String.fromCharCode(29));
    const hasParentheses = scanInput.includes('(');
    const hasC1Prefix = scanInput.startsWith(']C1');

    const looksLikeGS1 = hasInternalSeparators || hasParentheses || hasC1Prefix;

    let fullGS1Data = null;
    let hasGS1Data = false;

    if (looksLikeGS1) {
      console.log('🔍 GS1 structure detected, attempting GS1 parse...');
      fullGS1Data = parseGS1BarcodeFull(scanInput);
      hasGS1Data = !!(fullGS1Data.parsedAIs && Object.keys(fullGS1Data.parsedAIs).length > 0);
      console.log('📊 GS1 parse result - hasGS1Data:', hasGS1Data, 'parsedAIs:', fullGS1Data?.parsedAIs);
    } else {
      console.log('📝 No GS1 structure detected, treating as plain barcode');
    }

    if (hasGS1Data && fullGS1Data) {
      console.log('📊 Global scan: Detected GS1-128 barcode');

      const gs1Data: GS1ScanData = {
        sscc: fullGS1Data.sscc,
        gtin: fullGS1Data.gtin,
        itemNo: fullGS1Data.gtin,
        quantity: fullGS1Data.quantity,
        batchNo: fullGS1Data.batchNo,
        expiryDate: fullGS1Data.expiryDate || fullGS1Data.bestBeforeDate || fullGS1Data.useByDate
      };

      console.log('📋 Global scan: Extracted GS1 data:', gs1Data);

      const hasSSCC = !!gs1Data.sscc;
      const hasItem = !!(gs1Data.itemNo || gs1Data.gtin);

      // If no relevant GS1 data, treat as plain barcode
      if (!hasSSCC && !hasItem) {
        console.log('⚠️ GS1 detected but no SSCC/GTIN found - treating as plain barcode');
        const cleanedItemNo = scanInput.replace(/[~§]+$/g, '').trim();
        handleScan({ itemNo: cleanedItemNo });
      }
      // Pre-filled pallet scan (SSCC + Item)
      else if (hasSSCC && hasItem) {
        console.log('📦 Global scan: Pre-filled pallet scan detected (SSCC + Item)');
        handleScan(gs1Data);
      }
      // SSCC only - pallet management
      else if (hasSSCC && !hasItem) {
        console.log('📦 Global scan: SSCC-only scan - pallet management');
        let targetPallet = pallets.find(p => p.sscc === gs1Data.sscc);

        if (!targetPallet) {
          console.log('➕ Creating empty pallet with SSCC:', gs1Data.sscc);
          createEmptyPallet(gs1Data.sscc);
        } else {
          console.log('✅ Activating existing pallet:', targetPallet.sscc);
          setActivePalletSSCC(targetPallet.sscc);
        }
      }
      // Item only
      else if (!hasSSCC && hasItem) {
        console.log('🔎 Global scan: Item-only scan');
        handleScan(gs1Data);
      }
    } else {
      // Plain barcode
      console.log('📝 Global scan: Plain barcode (not GS1-128)');
      const cleanedItemNo = scanInput.replace(/[~§]+$/g, '').trim();
      console.log('🧹 Cleaned plain barcode:', cleanedItemNo);
      handleScan({ itemNo: cleanedItemNo });
    }
  };

  const handleReorder = async (oldIndex: number, newIndex: number) => {
    // Import arrayMove from @dnd-kit/sortable
    const { arrayMove } = await import('@dnd-kit/sortable');

    // Update local state immediately (optimistic update).
    // VIKTIG: oppdater også hver linjes `sorting` slik at den lokale rekkefølgen matcher det
    // som persisteres. Ellers vil et påfølgende plukk kjøre reorganizeLines (som sorterer på
    // `sorting`) og reversere flyttingen tilbake til opprinnelig rekkefølge.
    const reorderedLines = arrayMove(pickingLines, oldIndex, newIndex)
      .map((line, index) => ({ ...line, sorting: index + 1 }));
    setPickingLines(reorderedLines);

    // Save to backend
    try {
      await pickingService.updateLineOrder(
        pListId,
        reorderedLines.map((line, index) => ({
          pListLineNo: line.pListLineNo,
          sortOrder: index + 1
        }))
      );
    } catch (error) {
      console.error('Error saving line order:', error);
      // Revert on error
      await loadPickingDetails();
    }
  };

  // Flytt en linje nederst i plukkrekkefølgen. Gjenbruker handleReorder slik at den nye
  // rekkefølgen persisteres (PListLine.Sorting) — operatøren kan gå til lunsj og fortsette
  // uten å miste rekkefølgen. Tablet-vennlig alternativ til drag.
  const handleMoveToEnd = (lineNo: number) => {
    const idx = pickingLines.findIndex(l => l.pListLineNo === lineNo);
    if (idx !== -1 && idx !== pickingLines.length - 1) {
      handleReorder(idx, pickingLines.length - 1);
    }
  };

  // Multi-select «flytt valgte til slutt»: statussirkelen på hver ufullført linje
  // toggler markering; handlingslinjen over listen flytter alle markerte samlet.
  const [moveSelection, setMoveSelection] = useState<Set<number>>(new Set());

  const handleToggleMoveSelect = (lineNo: number) => {
    setMoveSelection(prev => {
      const next = new Set(prev);
      if (next.has(lineNo)) next.delete(lineNo); else next.add(lineNo);
      return next;
    });
  };

  // Flytter alle markerte linjer samlet til slutten — innbyrdes rekkefølge beholdes,
  // og persisteres via samme sti som drag (PListLine.Sorting).
  const handleMoveSelectedToEnd = async () => {
    if (moveSelection.size === 0) return;
    const kept = pickingLines.filter(l => !moveSelection.has(l.pListLineNo));
    const moved = pickingLines.filter(l => moveSelection.has(l.pListLineNo));
    const reordered = [...kept, ...moved].map((line, index) => ({ ...line, sorting: index + 1 }));
    setPickingLines(reordered);
    setMoveSelection(new Set());
    try {
      await pickingService.updateLineOrder(
        pListId,
        reordered.map((line, index) => ({
          pListLineNo: line.pListLineNo,
          sortOrder: index + 1
        }))
      );
    } catch (error) {
      console.error('Error saving line order:', error);
      await loadPickingDetails();
    }
  };

  // Auto-show image panel when active line changes and has an image (if enabled)
  useEffect(() => {
    // Only process if we have valid data and auto-show is enabled
    if (!autoShowImage || pickingLines.length === 0 || activeLineIndex === null) {
      if (!autoShowImage) {
        setShowImagePanel(false);
      }
      return;
    }

    const currentActiveLine = pickingLines[activeLineIndex];
    if (currentActiveLine?.image) {
      setShowImagePanel(true);
      setCurrentLineIndex(activeLineIndex);
    } else {
      setShowImagePanel(false);
    }
  }, [activeLineIndex, pickingLines, autoShowImage]);

  // Browser refresh warning - prevent loss of empty pallets
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (emptyPallets.length > 0) {
        e.preventDefault();
        e.returnValue = t('warehouse.modules.picking.alerts.unsavedEmptyPallets');
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [emptyPallets]);

  // ───────────────────────────────────────────────────────────────────
  // Punkt 2 (Norlux): Dialog for antall internkolli ved plukkstart
  // ───────────────────────────────────────────────────────────────────
  // Når 'picking.autoCreateAndPrintKolliOnPickStart' er på, vises en dialog
  // HVER gang operatøren åpner et plukk (også ved gjenopptak — ingen per-operatør-
  // sporing). Operatøren velger antall internkolli (default 1); ved bekreft
  // opprettes + skrives ut N kolli-etiketter. Avbryt/0 → ingen oppretting.
  //
  // Bruker SAMME backend som manuell kolli-utskrift (createAndPrintPackageLabels,
  // PickType=Standalone). Skriveren tas fra operatørens profil-konfig via
  // printingService. MERK: Dette er kolli (PListOrderPackages), IKKE SSCC-pall.
  // 'kolliDialogHandled' hindrer re-trigger ved re-render innen samme åpning;
  // den nullstilles ved remontering (= ny åpning av plukk).
  useEffect(() => {
    if (!autoKolliEnabled) return;          // Innstilling av → ingen dialog
    if (kolliDialogHandled) return;         // Allerede vist denne åpningen
    if (loading || !pickingDetails) return; // Vent på at plukk-data er lastet
    if (!user?.companyNo) return;
    // Samplukk har fått eskene sine ved opprettelsen (én per gruppe, skrevet ut fra
    // startbildet) — ingen ekstra kolli ved plukkstart (TW 36438546).
    if (pickingDetails.header.pListIsBatchYesNo === true) return;

    const orderNo = pickingDetails.lines?.[0]?.orderNo ?? 0;
    if (!orderNo) return; // Ingen ordre å knytte kolli til

    setKolliDialogHandled(true);
    setKolliCount('1');
    setShowKolliDialog(true);
  }, [autoKolliEnabled, kolliDialogHandled, loading, pickingDetails, user?.companyNo]);

  // Punkt 2: bekreft dialog → opprett + skriv ut N internkolli. Avbryt/0 → ingen oppretting.
  const handleConfirmKolliDialog = async () => {
    setShowKolliDialog(false);
    await printKolliLabels(parseInt(kolliCount, 10));
  };

  // Punkt 3: stille bekreftelse (transient inline-toast) for kolli-etikett-utskrift.
  const [kolliToast, setKolliToast] = useState<string | null>(null);
  const kolliToastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showKolliToast = (msg: string) => {
    setKolliToast(msg);
    if (kolliToastTimer.current) clearTimeout(kolliToastTimer.current);
    kolliToastTimer.current = setTimeout(() => setKolliToast(null), 4000);
  };

  // Samplukk: last eskene (med ordrene de tilhører) — brukes av skanningen og suppleringsesker.
  const loadBatchPackages = async (pListId: number) => {
    try {
      setBatchPackages(await pickingService.getPackagesForPList(pListId));
    } catch (err) {
      console.warn('Kunne ikke hente esker for samplukk:', err);
    }
  };

  useEffect(() => {
    if (pickingDetails?.header.pListIsBatchYesNo === true) {
      loadBatchPackages(pickingDetails.header.pListID);
    }
  }, [pickingDetails?.header.pListID, pickingDetails?.header.pListIsBatchYesNo]);

  // Ordrene som deler esken til en linje (samplukk). Linjen peker på startesken via packageNo.
  const getSharedOrderNos = (line?: { orderNo?: number; packageNo?: number | null }): number[] => {
    if (!line?.orderNo) return [];
    const pkg = batchPackages.find(p => p.packageNo === line.packageNo);
    return pkg?.orderNos?.length ? pkg.orderNos : [line.orderNo];
  };

  // Gyldige esker å skanne for en linje i samplukk: alle esker på linjens ordre.
  const getValidPackageNos = (line?: { orderNo?: number; packageNo?: number | null } | null): number[] => {
    if (!line) return [];
    const ids = batchPackages
      .filter(p => line.orderNo != null && (p.orderNos ?? [p.orderNo]).includes(line.orderNo))
      .map(p => p.packageNo);
    if (line.packageNo && !ids.includes(line.packageNo)) ids.push(line.packageNo);
    return ids;
  };

  // Felles: opprett + skriv ut N kolli-etiketter til operatørens default kollietikettskriver
  // (stille bekreftelse). Brukes av både plukkstart-dialogen (auto) og header-ikonets antalls-modal.
  // Kollien knyttes til ordren på AKTIV linje; i samplukk til alle ordrene som deler linjens eske.
  const printKolliLabels = async (n: number) => {
    if (!pickingDetails || !n || n < 1) return;
    const kolliLine = activeLine ?? pickingDetails.lines?.[0];
    const orderNo = kolliLine?.orderNo ?? 0;
    if (!orderNo) return;
    const isBatchPick = pickingDetails.header.pListIsBatchYesNo === true;
    try {
      const result = await printingService.createAndPrintPackageLabels({
        numberOfLabels: n,
        pListID: pickingDetails.header.pListID,
        orderNo,
        sharedOrderNos: isBatchPick ? getSharedOrderNos(kolliLine) : undefined,
        pickType: 'Standalone',
        customerName: kolliLine?.customerName,
        orderNoERP: kolliLine?.orderNoERP,
        printLabels: true,
      });
      if (isBatchPick) {
        await loadBatchPackages(pickingDetails.header.pListID);
      }
      showKolliToast(
        result.success
          ? t('warehouse.modules.picking.details.kolliLabelsPrinted', { 0: n.toString() }, `${n} kolli-etikett(er) skrevet ut`)
          : result.message || t('warehouse.modules.picking.alerts.kolliCreateFailed', {}, 'Kunne ikke skrive ut kolli-etikett')
      );
    } catch (err: any) {
      showKolliToast(err.message || t('warehouse.modules.picking.alerts.kolliCreateFailed', {}, 'Kunne ikke skrive ut kolli-etikett'));
    }
  };

  // Header-ikon (Tags): bekreft antall i modal → skriv ut. Default 1, OK bekrefter.
  const handleConfirmPrintKolliModal = async () => {
    setShowPrintKolliModal(false);
    await printKolliLabels(parseInt(printKolliCount, 10));
  };

  // Strukturvarer: aktiv linje beriket med «maks mulig sett»-begrensning, slik at
  // Aktiv plukklinje-kortet viser samme advarsel som linjelisten.
  const activeLineWithStructureCap = React.useMemo(() => {
    if (!activeLine) return null;
    const capped = applyStructureCaps(pickingLines.map(convertToPickingLine));
    return capped.find(l => l.pListLineNo === activeLine.pListLineNo) ?? convertToPickingLine(activeLine);
  }, [activeLine, pickingLines, convertToPickingLine]);

  // Strukturvarer: første plukkbare linje — hopper over hovedvare-gruppehoder (structureLineType=1)
  // og manko-linjer ([X]). Brukes som fallback der aktiv linje tidligere ble satt til index 0.
  // Når alt er plukket: fall tilbake til første linje som IKKE er et gruppehode (hovedvaren
  // skal aldri stå som aktiv plukklinje).
  const firstPickableIndex = (lineList: { isCompleted: boolean; binCode?: string; structureLineType?: number }[]): number => {
    const idx = lineList.findIndex(l =>
      !l.isCompleted && l.binCode !== '[X]' && (l.structureLineType ?? 0) !== 1);
    if (idx !== -1) return idx;
    const firstNonHeader = lineList.findIndex(l => (l.structureLineType ?? 0) !== 1);
    return firstNonHeader !== -1 ? firstNonHeader : 0;
  };

  // Handle line selection from list
  const handleLineSelect = (index: number) => {
    setActiveLineIndex(index);
    setCurrentLineIndex(index);

    const line = pickingLines[index];
    if (line?.image && autoShowImage) {
      setShowImagePanel(true);
    }
  };

  // Handle manual image show/hide button click (toggle)
  const handleShowImage = () => {
    if (activeLine?.image) {
      if (showImagePanel) {
        // If image is already showing, close it
        setShowImagePanel(false);
      } else {
        // If image is not showing, open it
        setShowImagePanel(true);
        setCurrentLineIndex(activeLineIndex);
      }
    }
  };

  /**
   * Helper function to show quantity confirmation with package scan check.
   * For batch picks with packageNo, shows PackageScanModal first.
   * Otherwise shows QuantityConfirmModal directly.
   */
  const showQuantityConfirmWithPackageCheck = (line: ExtendedPickingLine, quantity: number) => {
    setLineToConfirm(line);
    setQuantityToConfirm(quantity);
    setQuantityModalMode('add');

    // Check if this is a batch pick AND line has a package assigned
    const isBatchPick = pickingDetails?.header.pListIsBatchYesNo === true;
    const hasPackage = line.packageNo != null && line.packageNo > 0;

    if (isBatchPick && hasPackage) {
      // Show package scan modal first
      setShowPackageScan(true);
    } else {
      // Show quantity confirm directly
      setShowQuantityConfirm(true);
    }
  };

  /**
   * Åpner antall-modalen i setTotal-modus (redigering av plukket total).
   * Brukes fra blyant-affordancen på aktiv linje (fastpick på desktop, alltid på mobil).
   * Bekreftelse ruter til handleQuantityEdit — IKKE handleQuantityConfirm — slik at
   * pall-/telle-sideeffektene i skann-flyten ikke trigges.
   */
  const openSetTotalModal = (lineNo: number) => {
    const line = pickingLines.find(l => l.pListLineNo === lineNo);
    if (!line || line.itemType === 6) return; // ItemType=6: antall endres kun via serieplukk-modalen
    setLineToConfirm(line);
    // TW 36393699: bestilt antall når ingenting er plukket (ett trykk bekrefter normaltilfellet),
    // ellers dagens plukkede total.
    setQuantityToConfirm(line.qtyPicked > 0 ? line.qtyPicked : line.qty);
    setQuantityModalMode('setTotal');
    setShowQuantityConfirm(true);
  };

  const handleSetTotalConfirm = async (newTotal: number) => {
    if (!lineToConfirm) return;
    const lineNo = lineToConfirm.pListLineNo;

    setShowQuantityConfirm(false);
    setLineToConfirm(null);
    setQuantityToConfirm(0);
    setQuantityModalMode('add');

    await handleQuantityEdit(lineNo, newTotal);
  };

  /**
   * Handler when package scan is confirmed - proceed to quantity confirmation
   */
  const handlePackageScanConfirm = () => {
    setShowPackageScan(false);
    setShowQuantityConfirm(true);
  };

  /**
   * Handle barcode scan in location-scan-only mode.
   * When only BarCodeConfirmLocation is enabled, the operator scans location barcodes
   * to drive the picking flow instead of scanning items.
   */
  const handleLocationOnlyScan = async (scannedBarcode: string) => {
    if (!activeLine || activeLineIndex === null || !pickingDetails) return;

    // Allokeringsetikett gjenkjennes også i lokasjons-kun-modus (entydig ALC-prefiks).
    if (tryHandleAllocationScan(scannedBarcode)) return;

    // Allokert linje bekreftes KUN med allokeringsetiketten — et lokasjonsskann er ikke
    // nok til å garantere at de øremerkede varene tas. Uallokerte linjer uberørt.
    if (activeLine.allocationResID) {
      const alcCode = `ALC-${activeLine.allocationResID}`;
      showScanError(t('warehouse.modules.picking.alerts.allocationScanRequired', { 0: alcCode },
        `Linjen er allokert til en salgsordre — skann allokeringsetiketten (${alcCode}) på varene for å bekrefte.`));
      return;
    }

    setCurrentLineIndex(activeLineIndex);

    // Validate against raw BinCode (not the human-readable display name).
    // activeLine.location holds StcBin.Description1 ("Reol 9 vert 3 plass 3") for display,
    // while activeLine.binCode holds the actual barcode value ("9.3.3") printed on the bin label.
    const expectedBinCode = activeLine.binCode || '';
    const displayLocation = activeLine.location || expectedBinCode;

    // [X]-shortage: rute til overstyrings-modal i stedet for å la plukket fortsette
    // (backend avviser uansett med PICK_FROM_SHORTAGE_LINE). Defensiv — isLocationScanOnlyMode
    // ekskluderer allerede [X]-linjer, men hold sjekken her i tilfelle handleLocationOnlyScan
    // kalles via annen rute i fremtiden.
    if (expectedBinCode === '[X]') {
      handlePickLine(activeLine.pListLineNo, activeLine.qty);
      return;
    }

    // Match scanned value against active line's BinCode (case-insensitive)
    if (scannedBarcode.toLowerCase() !== expectedBinCode.toLowerCase()) {
      showScanError(t('warehouse.modules.picking.alerts.wrongLocation', { 0: displayLocation, 1: expectedBinCode, 2: scannedBarcode }));
      return;
    }

    // Lokasjon matcher — la handlePickLine håndtere obligatorisk-telling-gate sentralt
    // og videre ruting (serie/batch/antall). Telle-sjekken lå tidligere her, men er nå
    // flyttet til handlePickLine slik at den også kjører for profiler uten lokasjonsskann.
    playScanSuccess();
    handlePickLine(activeLine.pListLineNo, activeLine.qty);
  };

  // TW 36320099: fallback-oppslag mot Items/ItemUnits når skannet kode ikke matcher noen linje
  // direkte — lokalt registrerte strekkoder (ItemUnits) blir dermed gyldige også i plukk.
  // Serveren håndterer GTIN-13/14-variantene.
  const findLineByItemWithResolve = async (scannedItem: string): Promise<ExtendedPickingLine | null> => {
    const direct = findLineByItem(scannedItem);
    if (direct) return direct;
    try {
      const resolved = await itemsService.resolveBarcode(scannedItem);
      if (resolved.length > 0 && resolved[0].itemNo) {
        return findLineByItem(resolved[0].itemNo) ?? null;
      }
    } catch (err) {
      console.warn('Barcode resolve fallback failed:', err);
    }
    return null;
  };

  const handleScan = async (scanData: GS1ScanData) => {
    console.log('🔍 ModernPickingDetails: Processing scan with full GS1 data', scanData);

    const { itemNo, quantity, sscc, batchNo, expiryDate, gtin } = scanData;

    console.log('🔍 ModernPickingDetails: Destructured - itemNo=', itemNo, 'gtin=', gtin, 'sscc=', sscc);
    console.log('🔍 ModernPickingDetails: effectiveItemNo will be:', itemNo || gtin);

    // SCENARIO 1: SSCC-only scan (no item) - handled in GS1Scanner, shouldn't reach here
    if (sscc && !itemNo && !gtin) {
      console.log('⚠️ SSCC-only scan should be handled in GS1Scanner');
      return;
    }

    // SCENARIO 2: Pre-filled pallet scan (SSCC + Item in same barcode)
    // Store SSCC info and continue to location/quantity validation
    // The pallet will be created when user confirms quantity
    if (sscc && (itemNo || gtin)) {
      console.log('📦 Pre-filled pallet detected - storing SSCC for later pallet creation');
      // Store the full GS1 data for use after quantity confirmation
      setPendingGS1Data(scanData);

      // Check if pallet already exists
      let targetPallet = pallets.find(p => p.sscc === sscc);
      if (targetPallet) {
        console.log('✅ Pallet already exists, setting as active');
        setActivePalletSSCC(sscc);
      }
      // Continue to location/quantity validation below
      // The SSCC info in scanData will be used when quantity is confirmed
    }

    // Use GTIN if itemNo is not present
    const effectiveItemNo = itemNo || gtin;
    if (!effectiveItemNo) {
      console.log('⚠️ No item identifier found in scan');
      return;
    }

    // Helper function to check if scanned item matches line
    const itemsMatch = (scannedItem: string, line: ExtendedPickingLine): boolean => {
      const scannedLower = scannedItem.toLowerCase();
      const cleanedScanned = scannedItem.replace(/^0+/, '') || scannedItem;
      const cleanedScannedLower = cleanedScanned.toLowerCase();

      console.log(`🔎 Matching: scanned="${scannedItem}" (cleaned="${cleanedScanned}") against line.itemNo="${line.itemNo}", line.itemBarCode="${line.itemBarCode}"`);

      // Match against itemNo
      if (line.itemNo) {
        const itemNoLower = line.itemNo.toLowerCase();
        if (itemNoLower === scannedLower || itemNoLower === cleanedScannedLower) {
          console.log(`✅ MATCH on itemNo: ${line.itemNo}`);
          return true;
        }
      }

      // Match against itemBarCode
      if (line.itemBarCode) {
        const barCodeLower = line.itemBarCode.toLowerCase();
        if (barCodeLower === scannedLower || barCodeLower === cleanedScannedLower) {
          console.log(`✅ MATCH on itemBarCode: ${line.itemBarCode}`);
          return true;
        }
      }

      console.log(`❌ NO MATCH for this line`);
      return false;
    };

    // RAPID SCANNING MODE: Skip all dialogs and directly update quantity
    if (enableRapidScanning) {
      console.log('⚡ Rapid scanning mode enabled');

      // Find matching line (prefer incomplete lines) — med Items/ItemUnits-fallback
      const matchedLine = await findLineByItemWithResolve(effectiveItemNo);

      if (!matchedLine) {
        // Show error but don't block - just focus back on scanner
        console.error('❌ Item not found in picking list:', effectiveItemNo);
        showScanError(t('warehouse.modules.picking.alerts.itemNotInList'));
        return;
      }

      // Allokert linje bekreftes kun med ALC-etiketten — også i rapid-modus. Finnes en
      // uallokert linje med samme vare, tar den skannet i stedet (samme vare kan stå
      // både allokert og fritt på samme liste).
      let rapidLine = matchedLine;
      if (rapidLine.allocationResID) {
        const alternative = pickingLines.find(l =>
          l.qtyPicked < l.qty && !l.allocationResID && itemsMatch(effectiveItemNo, l));
        if (!alternative) {
          const alcCode = `ALC-${rapidLine.allocationResID}`;
          showScanError(t('warehouse.modules.picking.alerts.allocationScanRequired', { 0: alcCode },
            `Linjen er allokert til en salgsordre — skann allokeringsetiketten (${alcCode}) på varene for å bekrefte.`));
          return;
        }
        rapidLine = alternative;
      }

      // Set matched line as active
      const lineIndex = pickingLines.indexOf(rapidLine);
      setCurrentLineIndex(lineIndex);
      setActiveLineIndex(lineIndex);

      // Calculate new quantity (increment by 1)
      const newTotalQuantity = rapidLine.qtyPicked + 1;

      // Validate that we don't exceed ordered quantity
      if (newTotalQuantity > rapidLine.qty) {
        showScanError(t('warehouse.modules.picking.alerts.maxQuantityReached', { 0: rapidLine.qty }));
        return;
      }

      // Skann godkjent — gi umiddelbar lyd-kvittering før async-oppdateringen
      playScanSuccess();

      // 36334078: tempo-dom fra skannefeltet — tastet oppføring regnes som manuell
      pendingEntryMethodRef.current = scanData.typedManually
        ? { method: 'Manual' }
        : { method: 'Scanned', barcode: effectiveItemNo };

      // Store pending GS1 data for pallet creation
      if (sscc && (itemNo || gtin)) {
        setPendingGS1Data(scanData);

        // Create/activate pallet if needed
        let targetPallet = pallets.find(p => p.sscc === sscc);
        if (!targetPallet) {
          await createNewPallet(sscc, scanData);
        } else {
          setActivePalletSSCC(sscc);
        }
      }

      // Directly update quantity without any dialogs
      try {
        await handleUpdateQuantity(rapidLine.pListLineNo, newTotalQuantity);
        console.log('✅ Rapid scan completed successfully');
      } catch (error) {
        console.error('Error in rapid scan update:', error);
        playScanError();
        alert(t('warehouse.modules.picking.alerts.updateQuantityFailed'));
      }

      return;
    }

    // NORMAL MODE: Original logic with dialogs
    // Check if scanned item matches active line (case-insensitive with GTIN cleaning)
    if (activeLine && itemsMatch(effectiveItemNo, activeLine)) {
      // Allokert linje bekreftes kun med ALC-etiketten (garanterer at de øremerkede
      // varene tas) — vareskann avvises med hint. Uallokerte linjer uberørt.
      if (activeLine.allocationResID) {
        const alcCode = `ALC-${activeLine.allocationResID}`;
        showScanError(t('warehouse.modules.picking.alerts.allocationScanRequired', { 0: alcCode },
          `Linjen er allokert til en salgsordre — skann allokeringsetiketten (${alcCode}) på varene for å bekrefte.`));
        return;
      }
      playScanSuccess();
      // 36334078: tempo-dom fra skannefeltet — tastet oppføring regnes som manuell
      pendingEntryMethodRef.current = scanData.typedManually
        ? { method: 'Manual' }
        : { method: 'Scanned', barcode: effectiveItemNo };
      const activeIndex = activeLineIndex!;
      setCurrentLineIndex(activeIndex); // Set visual selection

      if (activeLine.image && autoShowImage) {
        setShowImagePanel(true);
      }

      const quantityToAdd = quantity || (activeLine.qty - activeLine.qtyPicked);

      // TODO: If we have batch/expiry from GS1, we should create OrderSSCC record here too
      console.log('Scanned item data:', { batchNo, expiryDate, gtin });

      if (activeLine.requireLocationScan) {
        // [X]-shortage: ikke vis Skann lokasjon (operatør kan ikke skanne [X]). handlePickLine
        // ruter selv til overstyrings-modalen.
        if (activeLine.binCode === '[X]') {
          handlePickLine(activeLine.pListLineNo, activeLine.qty);
        } else {
          setShowLocationScan(true);
          setTempGS1Quantity(quantityToAdd);
        }
      } else {
        // Route to serial/batch modal if needed, otherwise quantity confirmation
        handlePickLine(activeLine.pListLineNo, activeLine.qty);
      }
    } else {
      // Fallback to original logic for non-active line scanning — med Items/ItemUnits-fallback
      let matchedLine = await findLineByItemWithResolve(effectiveItemNo);

      // Allokert linje bekreftes kun med ALC-etiketten. Finnes en uallokert linje med
      // samme vare, tar den skannet i stedet; ellers avvises med hint.
      if (matchedLine?.allocationResID) {
        const alternative = pickingLines.find(l =>
          l.qtyPicked < l.qty && !l.allocationResID && itemsMatch(effectiveItemNo, l));
        if (!alternative) {
          const alcCode = `ALC-${matchedLine.allocationResID}`;
          showScanError(t('warehouse.modules.picking.alerts.allocationScanRequired', { 0: alcCode },
            `Linjen er allokert til en salgsordre — skann allokeringsetiketten (${alcCode}) på varene for å bekrefte.`));
          return;
        }
        matchedLine = alternative;
      }

      if (matchedLine) {
        playScanSuccess();
        // 36334078: tempo-dom fra skannefeltet — tastet oppføring regnes som manuell
        pendingEntryMethodRef.current = scanData.typedManually
          ? { method: 'Manual' }
          : { method: 'Scanned', barcode: effectiveItemNo };
        const lineIndex = pickingLines.indexOf(matchedLine);
        setCurrentLineIndex(lineIndex); // Set visual selection
        setActiveLineIndex(lineIndex); // Override active line

        if (matchedLine.image && autoShowImage) {
          setShowImagePanel(true);
        }

        const quantityToAdd = quantity || (matchedLine.qty - matchedLine.qtyPicked);

        if (matchedLine.requireLocationScan) {
          // [X]-shortage: ikke vis Skann lokasjon (operatør kan ikke skanne [X]).
          if (matchedLine.binCode === '[X]') {
            handlePickLine(matchedLine.pListLineNo, matchedLine.qty);
          } else {
            setShowLocationScan(true);
            setTempGS1Quantity(quantityToAdd);
          }
        } else {
          // Route to serial/batch modal if needed, otherwise quantity confirmation
          handlePickLine(matchedLine.pListLineNo, matchedLine.qty);
        }
      } else {
        showScanError(t('warehouse.modules.picking.alerts.itemNotInList'));
      }
    }
  };

  const handleMandatoryCountingSubmit = async (countedQuantity: number) => {
    // Bruk plukk-konteksten som gaten lagret (robust mot overstyrte linjer og
    // tilfeller der currentLine ikke er satt, f.eks. manuell plukk-knapp).
    const pending = pendingPickAfterCount;
    const lineForCount = pending?.line ?? currentLine;
    if (!lineForCount || !pickingDetails || !mandatoryCountingData) return;

    setIsSubmittingCounting(true);
    try {
      const companyNo = pickingDetails.header.companyNo;
      await mandatoryCountingService.submitMandatoryCounting(companyNo, {
        warehouseID: lineForCount.warehouseID,
        itemNo: mandatoryCountingData.itemNo,
        binCode: mandatoryCountingData.binCode,
        countedQuantity: countedQuantity,
        pListID: pListId,
        pListLineNo: lineForCount.pListLineNo
      });

      // Close mandatory counting modal
      setShowMandatoryCounting(false);
      setMandatoryCountingData(null);
      setPendingPickAfterCount(null);

      // Continue with normal picking flow - route to serial/batch modal if needed.
      // skipCountCheck=true: LastCountedDate er nettopp oppdatert, så ikke spør på nytt.
      setTempGS1Quantity(null);
      if (pending) {
        handlePickLine(pending.lineNo, pending.qty, pending.line, true);
      } else {
        handlePickLine(lineForCount.pListLineNo, lineForCount.qty, undefined, true);
      }
    } catch (error) {
      console.error('Error submitting mandatory counting:', error);
      alert(t('warehouse.modules.picking.alerts.registerCountingFailed'));
    } finally {
      setIsSubmittingCounting(false);
    }
  };

  // Strukturplukk: åpne telle-snarveien for en begrenset komponentlinje.
  const handleStructureCountRequest = (lineNo: number) => {
    const line = pickingLines.find(l => l.pListLineNo === lineNo);
    if (!line || !line.binCode || line.binCode === '[X]') return;

    setStructureCountData({
      pListLineNo: line.pListLineNo,
      itemNo: line.itemNo || '',
      itemDescription: line.itemDescription || '',
      binCode: line.binCode,
      warehouseID: line.warehouseID,
      currentQuantity: Math.max(0, line.binAvailableQty ?? 0)
    });
  };

  // Strukturplukk: registrer tellingen og last plukket på nytt slik at dekningen
  // (binAvailableQty → «maks mulig sett») rekalkuleres og varslene oppdateres.
  const handleStructureCountSubmit = async (countedQuantity: number) => {
    if (!structureCountData || !pickingDetails) return;

    setIsSubmittingStructureCount(true);
    try {
      const companyNo = pickingDetails.header.companyNo;
      const countedLineNo = structureCountData.pListLineNo;
      await mandatoryCountingService.submitMandatoryCounting(companyNo, {
        warehouseID: structureCountData.warehouseID,
        itemNo: structureCountData.itemNo,
        binCode: structureCountData.binCode,
        countedQuantity: countedQuantity,
        pListID: pListId,
        pListLineNo: countedLineNo
      });

      setStructureCountData(null);
      // Behold fokus på den talte linjen — operatøren står fysisk på lokasjonen og skal
      // fortsette plukket der, selv om re-sorteringen ellers ville flyttet en annen linje øverst.
      await loadPickingDetails(userProfile, countedLineNo);
    } catch (error) {
      console.error('Error submitting structure count:', error);
      alert(t('warehouse.modules.picking.alerts.registerCountingFailed'));
    } finally {
      setIsSubmittingStructureCount(false);
    }
  };

  const handleLocationScanConfirm = async (scannedValue?: string) => {
    if (!currentLine || !pickingDetails) return;

    // Validate against raw BinCode (not the human-readable display name).
    // currentLine.location holds StcBin.Description1 ("Reol 9 vert 3 plass 3") for display,
    // while currentLine.binCode holds the actual barcode value ("9.3.3") printed on the bin label.
    const expectedBinCode = currentLine.binCode || '';
    const displayLocation = currentLine.location || expectedBinCode;

    // Punkt 4: global skann i LocationScanModal sender skannet verdi direkte (unngår setState-timing).
    const effectiveScan = scannedValue ?? locationScan;
    if (effectiveScan.toLowerCase() === expectedBinCode.toLowerCase() || overrideLocation) {
      setShowLocationScan(false);
      setLocationScan('');
      setOverrideLocation('');

      setTempGS1Quantity(null);

      // handlePickLine håndterer nå obligatorisk-telling-gate sentralt + videre ruting.
      handlePickLine(currentLine.pListLineNo, currentLine.qty);
    } else {
      alert(t('warehouse.modules.picking.alerts.wrongLocation', { 0: displayLocation, 1: expectedBinCode, 2: locationScan }));
    }
  };

  const handleLocationOverride = async () => {
    setShowLocationOverride(false);
    setShowLocationScan(false);

    if (currentLine && overrideLocation) {
      try {
        // Update BinCode in database
        await pickingService.updateBinCode(pListId, currentLine.pListLineNo, overrideLocation);

        // Update the location on the picking line
        const updatedLines = pickingLines.map(l =>
          l.pListLineNo === currentLine.pListLineNo ? { ...l, location: overrideLocation, binCode: overrideLocation } : l
        );
        setPickingLines(updatedLines);

        // Update currentLine reference with the new location
        const updatedCurrentLine = { ...currentLine, location: overrideLocation, binCode: overrideLocation };

        setTempGS1Quantity(null);

        // Pass updated line to bypass stale pickingLines-state ([X]-check ville ellers
        // re-åpnet modalen siden React-state ennå har gammel binCode).
        handlePickLine(updatedCurrentLine.pListLineNo, updatedCurrentLine.qty, updatedCurrentLine);
      } catch (error) {
        console.error('Error updating location override:', error);
        alert(t('warehouse.modules.picking.alerts.updateLocationFailed'));
      }
    }
    setOverrideLocation('');
  };

  // Shortage-resolution for [X]-linjer: operatør har visuelt bekreftet at varen finnes
  // på en lokasjon Compass ikke har saldo for. Backend gjør intern korreksjon (ikke til ERP)
  // og oppdaterer PListLine.BinCode. Etterpå går vi videre til vanlig plukk-flyt.
  const handleResolveShortage = async (payload: {
    newBinCode: string;
    discoveredQuantity: number;
    reasonCodeNo: number;
    comment?: string;
  }) => {
    if (!currentLine) return;

    await pickingService.relocateWithDiscovery(pListId, currentLine.pListLineNo, payload);

    // Oppdater linjen lokalt — backend har satt BinCode til newBinCode og opprettet reservasjon
    const updatedLines = pickingLines.map(l =>
      l.pListLineNo === currentLine.pListLineNo
        ? { ...l, location: payload.newBinCode, binCode: payload.newBinCode }
        : l
    );
    setPickingLines(updatedLines);
    const updatedCurrentLine = { ...currentLine, location: payload.newBinCode, binCode: payload.newBinCode };

    // Lukk modaler og fortsett til vanlig plukk-flyt
    setShowLocationOverride(false);
    setShowLocationScan(false);
    setOverrideLocation('');
    setTempGS1Quantity(null);

    // Pass updated line for å unngå at [X]-sjekken i handlePickLine re-åpner modalen
    // (pickingLines-state er stale rett etter setPickingLines).
    handlePickLine(updatedCurrentLine.pListLineNo, updatedCurrentLine.qty, updatedCurrentLine);
  };

  // Check if few-point/zero-point counting should trigger after pick
  const checkIfFewPointCounting = async (
    itemNo: string,
    binCode: string,
    pickedQuantity: number
  ): Promise<{ shouldCount: boolean; remainingAfterPick: number }> => {
    if (!itemNo || !binCode) return { shouldCount: false, remainingAfterPick: 0 };

    try {
      // Get current ItemBins quantity for this item at this location
      const itemBins = await dataInspectionService.getItemBinsByItemNo(itemNo);

      // Summér ALLE ItemBins-rader for lokasjonen, ikke bare den første. Serie- og
      // batchvarer har én rad per serienr/batch på samme vare+lokasjon, så .find()
      // ville lest bare første rad (typisk 1 stk for serienummer) og gitt en altfor
      // lav forventet rest.
      const binRows = itemBins.filter((bin) => bin.binCode === binCode);

      if (binRows.length === 0) {
        console.log('⚠️ No ItemBins data found for', itemNo, 'at', binCode);
        return { shouldCount: false, remainingAfterPick: 0 };
      }

      // Check if remaining quantity after pick is at or below the threshold
      // threshold=0: triggers only when location becomes empty (legacy zero-point)
      // threshold=2: triggers when 2 or fewer items remain after pick
      //
      // VIKTIG: bruk rå `quantity`, IKKE `quantityAvailable`. Available er netto etter
      // reservasjon (Quantity − StcReservation), og reservasjonen på plukklinjen ER de
      // enhetene som plukkes. Å trekke pickedQuantity fra available trakk derfor samme
      // mengde to ganger: en lokasjon med 204 stk der 198 er reservert og plukkes fikk
      // max(0, (204−198) − 198) = 0 → dialogen påsto at lokasjonen skulle være tom,
      // mens 6 stk faktisk sto igjen. Fysisk rest etter plukk er Quantity − plukket.
      const binQuantity = binRows.reduce((sum, bin) => sum + (bin.quantity ?? 0), 0);
      const remainingAfterPick = Math.max(0, binQuantity - pickedQuantity);
      const shouldCount = remainingAfterPick <= fewPointCountingThreshold;

      console.log('🔍 Few-point counting check:', {
        itemNo,
        binCode,
        binQuantity,
        binRows: binRows.length,
        pickedQuantity,
        remainingAfterPick,
        threshold: fewPointCountingThreshold,
        shouldCount
      });

      return { shouldCount, remainingAfterPick };
    } catch (error) {
      console.error('Error checking few-point counting:', error);
      return { shouldCount: false, remainingAfterPick: 0 };
    }
  };

  // Handle zero-point counting - confirm zero
  const handleZeroPointConfirmZero = async () => {
    if (!zeroPointCountingData || !pickingDetails) return;

    setIsSubmittingZeroPoint(true);
    try {
      // Vern mot utilsiktet nullstilling: en «bekreft tomt» skal aldri kunne overskrive
      // en lokasjon som Compass mener har saldo igjen etter plukket. Modalen viser
      // nullpunkt-varianten kun når expectedRemaining <= 0, så dette skal ikke kunne
      // inntreffe — men telleresultatet skrives absolutt til ItemBins og eksporteres
      // til ERP umiddelbart, så en regnefeil i forventet rest må ikke kunne nulle
      // reell saldo. Er det saldo igjen, registrer den i stedet for 0.
      const safeCountedQuantity = Math.max(0, zeroPointCountingData.expectedRemaining ?? 0);
      if (safeCountedQuantity > 0) {
        console.warn(
          '⚠️ Zero-point: «bekreft tomt» avvist — forventet rest er',
          safeCountedQuantity,
          'for', zeroPointCountingData.itemNo, 'på', zeroPointCountingData.binCode,
          '— registrerer forventet rest i stedet for 0.'
        );
      }

      // Register count via mandatoryCountingService (same as mandatory counting)
      const companyNo = pickingDetails.header.companyNo;
      await mandatoryCountingService.submitMandatoryCounting(companyNo, {
        warehouseID: zeroPointCountingData.warehouse,
        itemNo: zeroPointCountingData.itemNo,
        binCode: zeroPointCountingData.binCode,
        countedQuantity: safeCountedQuantity, // Zero-point confirmed (0 = tom lokasjon)
        pListID: pListId,
        pListLineNo: zeroPointCountingData.pListLineNo
      });

      console.log('✅ Zero-point count registered for', zeroPointCountingData.binCode);

      // Close modal
      setShowZeroPointCounting(false);
      setZeroPointCountingData(null);
    } catch (error) {
      console.error('Error registering zero-point count:', error);
      alert(t('warehouse.modules.picking.alerts.registerZeroPointFailed'));
    } finally {
      setIsSubmittingZeroPoint(false);
    }
  };

  // Handle zero-point counting - submit deviation
  const handleZeroPointSubmitDeviation = async (countedQuantity: number) => {
    if (!zeroPointCountingData || !pickingDetails) return;

    setIsSubmittingZeroPoint(true);
    try {
      // Register count with deviation via mandatoryCountingService (same as mandatory counting)
      const companyNo = pickingDetails.header.companyNo;
      await mandatoryCountingService.submitMandatoryCounting(companyNo, {
        warehouseID: zeroPointCountingData.warehouse,
        itemNo: zeroPointCountingData.itemNo,
        binCode: zeroPointCountingData.binCode,
        countedQuantity: countedQuantity, // Actual counted quantity (deviation from expected 0)
        pListID: pListId,
        pListLineNo: zeroPointCountingData.pListLineNo
      });

      console.log('✅ Zero-point deviation registered:', countedQuantity, 'at', zeroPointCountingData.binCode);

      // Close modal
      setShowZeroPointCounting(false);
      setZeroPointCountingData(null);
    } catch (error) {
      console.error('Error registering zero-point deviation:', error);
      alert(t('warehouse.modules.picking.alerts.registerDeviationFailed'));
    } finally {
      setIsSubmittingZeroPoint(false);
    }
  };

  /**
   * Resttelling-intercept: hvis telle-gaten armerte count-back for denne linjen, vis
   * rest-modalen NÅ (etter at plukkantallet er bekreftet) og utsett registreringen av
   * plukket til tellingen er sendt. Rekkefølgen mot backend er dermed identisk med
   * tell-før-plukk: telling (totalsaldo = rest + plukket) → plukkregistrering.
   */
  const handleQuantityConfirm = async (confirmedQuantity: number) => {
    if (!lineToConfirm) return;

    if (
      armedCountBack &&
      armedCountBack.pListLineNo === lineToConfirm.pListLineNo &&
      armedCountBack.binCode === (lineToConfirm.binCode || '')
    ) {
      setShowQuantityConfirm(false);
      setRestCountPending({ confirmedQuantity });
      return; // Registrering fortsetter i handleRestCountSubmit.
    }

    await handleQuantityConfirmCore(confirmedQuantity);
  };

  /**
   * Resttelling: operatøren har talt det som ligger igjen på lokasjonen etter plukket.
   * Talt totalsaldo = rest + plukket antall (før dette plukket) sendes via samme
   * submit-endepunkt som tell-før-plukk, og deretter registreres selve plukket.
   */
  const handleRestCountSubmit = async (restQuantity: number) => {
    if (!armedCountBack || !restCountPending || !lineToConfirm || !pickingDetails) return;

    const confirmedQuantity = restCountPending.confirmedQuantity;
    setIsSubmittingCounting(true);
    try {
      const companyNo = pickingDetails.header.companyNo;
      await mandatoryCountingService.submitMandatoryCounting(companyNo, {
        warehouseID: armedCountBack.warehouseID,
        itemNo: armedCountBack.itemNo,
        binCode: armedCountBack.binCode,
        countedQuantity: restQuantity + confirmedQuantity,
        pListID: pListId,
        pListLineNo: armedCountBack.pListLineNo
      });

      setRestCountPending(null);
      setArmedCountBack(null);

      // Registrer plukket — samme kjerneflyt som uten telling (pall, antall), men uten
      // null-/fåpunktstelling: operatøren har nettopp talt resten på lokasjonen, så en ny
      // punkttelling ville spurt om det samme tallet en gang til (Norlux, 28.10).
      await handleQuantityConfirmCore(confirmedQuantity, { skipPointCount: true });
    } catch (error) {
      console.error('Error submitting count-back counting:', error);
      alert(t('warehouse.modules.picking.alerts.registerCountingFailed'));
    } finally {
      setIsSubmittingCounting(false);
    }
  };

  const handleQuantityConfirmCore = async (
    confirmedQuantity: number,
    options: { skipPointCount?: boolean } = {}
  ) => {
    if (!lineToConfirm) return;

    const newTotalQuantity = lineToConfirm.qtyPicked + confirmedQuantity;

    // If we have pending GS1 data with SSCC, create/activate pallet first
    if (pendingGS1Data?.sscc) {
      console.log('📦 Creating/activating pallet from pending GS1 data:', pendingGS1Data.sscc);

      // Check if pallet already exists
      let targetPallet = pallets.find(p => p.sscc === pendingGS1Data.sscc);
      if (!targetPallet) {
        // Create new pallet with GS1 data
        await createNewPallet(pendingGS1Data.sscc, pendingGS1Data);
      } else {
        // Activate existing pallet
        setActivePalletSSCC(pendingGS1Data.sscc);
      }
    }

    // Check if few-point counting should trigger (only if zero-point/few-point counting is enabled
    // and the location was not just counted by the rest count).
    // Use raw binCode for ItemBins lookup, not the human-readable description.
    const fewPointResult = zeroPointCountingEnabled && !options.skipPointCount && lineToConfirm.itemNo && lineToConfirm.binCode
      ? await checkIfFewPointCounting(
          lineToConfirm.itemNo,
          lineToConfirm.binCode,
          confirmedQuantity
        )
      : { shouldCount: false, remainingAfterPick: 0 };

    await handleUpdateQuantity(lineToConfirm.pListLineNo, newTotalQuantity);


    setShowQuantityConfirm(false);
    setLineToConfirm(null);
    setQuantityToConfirm(0);

    // Clear pending GS1 data after processing
    setPendingGS1Data(null);

    // If few-point/zero-point counting triggered, show counting modal
    if (fewPointResult.shouldCount && lineToConfirm.warehouseID) {
      setZeroPointCountingData({
        itemNo: lineToConfirm.itemNo!,
        itemDescription: lineToConfirm.itemDescription || '',
        binCode: lineToConfirm.binCode || '',
        pickedQuantity: confirmedQuantity,
        expectedRemaining: fewPointResult.remainingAfterPick,
        warehouse: lineToConfirm.warehouseID,
        pListLineNo: lineToConfirm.pListLineNo
      });
      setShowZeroPointCounting(true);
    }
  };

  // «Bekreft tomt» (Ariens): meld at aktiv plukklinje er fysisk tom → backend konverterer den
  // til manko ([X]) og frigjør reservasjonen, deretter lastes lista på nytt (linjen havner i
  // manko-seksjonen nederst). ItemBins røres ikke — avviket flagges, ikke auto-korrigeres.
  const handleReportMissing = async () => {
    if (!lineToConfirm) return;
    try {
      await pickingService.reportLineMissing(pListId, lineToConfirm.pListLineNo);
      setShowQuantityConfirm(false);
      setLineToConfirm(null);
      setQuantityToConfirm(0);
      await loadPickingDetails();
    } catch (err) {
      console.error('Error reporting line missing:', err);
      alert(err instanceof Error ? err.message : 'Kunne ikke melde linje tom.');
    }
  };

  /**
   * Sentral sjekk for obligatorisk telling. Kjøres for ALLE plukk via handlePickLine,
   * uavhengig av om profilen krever lokasjonsskann (BarCodeConfirmLocation). Tidligere
   * lå denne sjekken kun i lokasjonsskann-grenene, slik at profiler uten lokasjonsskann
   * aldri fikk tellevarsel selv om terskelen (MandatoryCountingWeeks) var passert.
   *
   * Returnerer true hvis telling kreves og modalen er vist — kalleren skal da stoppe og
   * la handleMandatoryCountingSubmit gjenoppta plukket via pendingPickAfterCount.
   */
  const checkAndPromptMandatoryCounting = async (
    line: ExtendedPickingLine,
    lineNo: number,
    qty: number
  ): Promise<boolean> => {
    if (!pickingDetails) return false;

    // Bruk rå binCode (ikke visningsnavnet i line.location). [X]-linjer og tomme
    // lokasjoner har ingen reell bin å telle.
    const binCode = line.binCode || '';
    if (!binCode || binCode === '[X]') return false;

    try {
      const companyNo = pickingDetails.header.companyNo;
      const checkResult = await mandatoryCountingService.checkMandatoryCounting(companyNo, {
        warehouseId: line.warehouseID,
        itemNo: line.itemNo || '',
        binCode,
      });

      if (checkResult.requiresCounting) {
        // Resttelling (pick-first): utsett tellemodalen til etter at plukkantallet er
        // bekreftet i kvantumsmodalen. Gjelder kun standard antall-plukk — serie-/batchvarer
        // (ItemType 4/5/6) registrerer plukk inkrementelt i egne modaler og beholder derfor
        // tell-før-plukk, ellers ville tellingen havnet ETTER saldobevegelsen og gitt en
        // annen eksport enn tell-før-plukk (invariant-brudd, se TW 36358995).
        const isSerialOrBatch = line.itemType === 4 || line.itemType === 5 || line.itemType === 6;
        if (checkResult.pickFirstMode && !isSerialOrBatch) {
          setArmedCountBack({
            pListLineNo: lineNo,
            itemNo: line.itemNo || '',
            itemDescription: line.itemDescription || line.itemNo || '',
            binCode,
            warehouseID: line.warehouseID,
            lastCountedDate: checkResult.lastCountedDate,
            daysSinceLastCounted: checkResult.daysSinceLastCounted,
            currentQuantity: checkResult.currentQuantity,
          });
          return false; // Plukkflyten fortsetter; rest-modal vises i handleQuantityConfirm.
        }

        setMandatoryCountingData({
          itemNo: line.itemNo || '',
          itemDescription: line.itemDescription || line.itemNo || '',
          binCode,
          lastCountedDate: checkResult.lastCountedDate,
          daysSinceLastCounted: checkResult.daysSinceLastCounted,
          currentQuantity: checkResult.currentQuantity,
        });
        setPendingPickAfterCount({ lineNo, qty, line });
        setShowMandatoryCounting(true);
        return true;
      }

      // Telling ikke (lenger) påkrevd — fjern ev. gammel armering fra et avbrutt forsøk.
      setArmedCountBack(null);
    } catch (error) {
      console.error('Error checking mandatory counting:', error);
      // Ikke blokker plukk hvis sjekken feiler (samme defensive oppførsel som før).
    }
    return false;
  };

  const handlePickLine = async (
    lineNo: number,
    qty: number,
    lineOverride?: ExtendedPickingLine,
    skipCountCheck = false
  ) => {
    // lineOverride brukes etter state-mutasjoner (override / shortage-resolution) hvor
    // pickingLines-state ennå er stale pga. React's async batching. Da har callere allerede
    // ferske data på den oppdaterte linjen og må slippe forbi [X]-sjekken under.
    const line = lineOverride ?? pickingLines.find(l => l.pListLineNo === lineNo);
    if (!line) return;

    // [X]-shortage: Compass kjenner ikke en gyldig lokasjon for denne linjen.
    // Backend avviser plukk fra [X]-linje (PICK_FROM_SHORTAGE_LINE / KRIT-007), så vi
    // ruter operatør direkte til overstyrings-modalen i stedet for kvittum-bekreftelse.
    // Operatør kan da enten (a) overstyre til en bin som har saldo, eller (b) bruke
    // shortage-resolution-flyten (visuell telling + ReasonCode) for å korrigere saldo
    // før plukket.
    if (line.binCode === '[X]') {
      const lineIndex = pickingLines.findIndex(l => l.pListLineNo === lineNo);
      setCurrentLineIndex(lineIndex);
      setOverrideLocation('');
      setShowLocationOverride(true);
      return;
    }

    // Sentral obligatorisk-telling-gate: kjøres for hvert plukk uavhengig av
    // lokasjonsskann-innstilling. skipCountCheck=true når plukket gjenopptas rett
    // etter at telling nettopp er registrert (unngår å spørre på nytt).
    if (!skipCountCheck && await checkAndPromptMandatoryCounting(line, lineNo, qty)) {
      return; // Modal vist; plukk gjenopptas i handleMandatoryCountingSubmit.
    }

    // ItemType=6: Serial-tracked items in inventory - show serial pick modal
    if (line.itemType === 6 && line.qtyPicked < line.qty) {
      setSerialPickLine(line);
      setShowSerialPickModal(true);
      return;
    }

    // ItemType=4/5: Batch items - show batch pick modal
    if ((line.itemType === 4 || line.itemType === 5) && line.qtyPicked < line.qty) {
      setBatchPickLine(line);
      setShowBatchPickModal(true);
      return;
    }

    const quantityToAdd = qty - line.qtyPicked;
    // Show quantity confirmation modal (with package scan check for batch picks)
    showQuantityConfirmWithPackageCheck(line, quantityToAdd);
  };

  /**
   * Antallet som allerede ligger på raden backend vil oppdatere for (pall, ordrelinje, batch).
   * Backend overskriver Quantity ved upsert, så skann/blyant som bare sender økningen må legge
   * dette til — ellers erstatter et nytt skann på samme pall det forrige (felt 5 i ERP-eksporten
   * ble da bare siste skann).
   */
  const getExistingPalletRowQty = async (sscc: string, line: ExtendedPickingLine, itemBatchNo: string) =>
    (await pickingService.getOrderSSCCs(pListId))
      .filter(r => r.sscc === sscc && isPalletRowForLine(r, line) && (r.itemBatchNo || '') === itemBatchNo)
      .reduce((sum, r) => sum + r.quantity, 0);

  /**
   * Etter en feilet antallsoppdatering vet vi ikke hvor langt serveren kom — plukket og pallraden
   * kan være lagret selv om svaret aldri nådde nettbrettet (nettbrudd). Hent derfor linjer og paller
   * på nytt i stedet for å la operatøren bekrefte på nytt mot et utdatert lokalt bilde: da ble
   * pallraden lagt sammen to ganger (Haucon ordre 775462: 9 plukket, 18 på pall og i ERP-filen).
   * Feiler også innlastingen, viser loadPickingDetails feilskjermen med «Prøv igjen», så det
   * utdaterte bildet kan ikke bekreftes mot.
   */
  const resyncAfterFailedUpdate = async (lineNo: number) => {
    await loadPickingDetails(undefined, lineNo);
    await loadPallets();
  };

  /**
   * @param skipPalletRow true når kalleren allerede har registrert pallraden selv (GS1-forhåndsutfylt
   *   pall i createNewPallet) — da oppdateres bare plukket antall, ellers ble antallet lagt på pall to ganger.
   */
  const handleUpdateQuantity = async (lineNo: number, newTotalQty: number, skipPalletRow: boolean = false) => {
    console.log('🔄 Updating quantity for line:', lineNo, 'newTotalQty:', newTotalQty);

    const line = pickingLines.find(l => l.pListLineNo === lineNo);
    if (!line) return;

    // Overplukk-sperre — oppheves av ERP-flagget / manuell linje (TW 36401594, bizClick-regel)
    if (newTotalQty > line.qty && !isOverPickAllowed(line)) {
      alert(t('warehouse.modules.picking.alerts.quantityExceedsOrdered', { 0: line.qty }));
      return;
    }

    try {
      // Get StartDateTime for this line from state
      const startDateTime = lineStartTimes.get(lineNo);

      // 36334078: kanalen inndata kom fra — satt av skann-handlerne; null = manuell vei.
      const pendingEntry = pendingEntryMethodRef.current;
      pendingEntryMethodRef.current = null;
      const entryMethod = pendingEntry?.method ?? 'Manual';

      // Update picked quantity in PListLine - send the new total quantity and StartDateTime
      await pickingService.updatePickedQuantity(pListId, lineNo, newTotalQty, undefined, startDateTime, undefined, entryMethod, pendingEntry?.barcode);

      // Check if active pallet is empty (frontend-only) - will be converted
      const isConvertingEmptyPallet = activePallet && isEmptyPallet(activePallet);

      if (isConvertingEmptyPallet) {
        console.log('🎉 Converting empty pallet to database pallet:', activePallet.sscc);
      }

      // If there's an active pallet and new quantity > 0, save to OrderSSCC
      if (activePallet && newTotalQty > 0 && !skipPalletRow) {
        const line = pickingLines.find(l => l.pListLineNo === lineNo);
        if (line) {
          const quantityForPallet = newTotalQty - line.qtyPicked; // Only the new quantity for this operation
          if (quantityForPallet > 0) {
            // Use batch/expiry data from pendingGS1Data if available.
            // Tom batch/ingen GTIN er bevisst: etiketten henter ekte GTIN fra Items.ItemBarCode
            // (resolver-fallback) og batch fylles kun når den faktisk er kjent (GS1-skann).
            // Operatøren kan berike pallen manuelt etterpå (rediger pall).
            const batchNo = pendingGS1Data?.batchNo || '';
            const expiryDate = pendingGS1Data?.expiryDate || null;

            console.log('📋 Creating OrderSSCC with batch/expiry:', { batchNo, expiryDate });

            // Backend overskriver raden — legg til det som allerede ligger der fra tidligere skann
            const existingRowQty = await getExistingPalletRowQty(activePallet.sscc, line, batchNo);

            const ssccRequest: CreateOrderSSCCRequest = {
              companyNo: '', // Will be set by backend
              pListID: pListId,
              orderNo: line.orderNo,
              lineNo: palletRowLineNo(line), // ordrelinjens LineNo — ikke pListLineNo (eksporten joiner mot OrderLine)
              sscc: activePallet.sscc,
              gtin: pendingGS1Data?.gtin || undefined,
              quantity: existingRowQty + quantityForPallet,
              ssccLevel: 1,
              itemBatchNo: batchNo,
              itemBatchExpDate: expiryDate,
              stackingWeight: 0,
              grossWeight: 0, // backend beregner: Quantity × OrderLine.Weight (36334224)
              locationName: line.binCode || line.location,
              transitPackagingID: carrierIdForPallet(activePallet.sscc)
            };

            await pickingService.createOrUpdateSSCC(pListId, ssccRequest);
          }
        }
      }

      // Reload pallets from database to get updated data (will convert empty pallet automatically)
      await loadPallets();
      
      // Update local state
      const updatedLines = pickingLines.map(line => {
        if (line.pListLineNo === lineNo) {
          const isFullyPicked = newTotalQty >= line.qty;
          const isPartiallyPicked = newTotalQty > 0 && newTotalQty < line.qty;
          
          return {
            ...line,
            qtyPicked: newTotalQty,
            isCompleted: newTotalQty > 0,
            isPartiallyPicked,
            isFullyPicked,
            palletId: activePallet?.sscc
          };
        }
        return line;
      });
      
      // Find the line that was just updated
      const lineIndex = updatedLines.findIndex(l => l.pListLineNo === lineNo);
      const line = updatedLines[lineIndex];
      
      if (lineIndex !== -1 && line && line.isFullyPicked && lineIndex === activeLineIndex) {
        // Line is fully picked and is the active line - reorganize to move it to bottom
        setTimeout(() => {
          const reorganized = reorganizeLines(updatedLines);
          setPickingLines(reorganized);

          // Force activeLineIndex to change to trigger useEffect
          // First set to null, then to next pickable in next tick
          setActiveLineIndex(null);
          setTimeout(() => setActiveLineIndex(firstPickableIndex(reorganized)), 0);
        }, 300);
      } else if (lineIndex !== -1 && line && !line.isFullyPicked) {
        // Partially picked - keep as active, reorganize if needed.
        // Re-finn linjen etter reorganisering (påbegynte linjer flyttes til midtgruppen)
        // slik at aktiv linje følger sist skannede vare — ikke hopper til toppen.
        const reorganized = reorganizeLines(updatedLines);
        setPickingLines(reorganized);
        const reorganizedIndex = reorganized.findIndex(l => l.pListLineNo === lineNo);
        setActiveLineIndex(reorganizedIndex !== -1 ? reorganizedIndex : firstPickableIndex(reorganized));
        setCurrentLineIndex(reorganizedIndex !== -1 ? reorganizedIndex : firstPickableIndex(reorganized));
      } else {
        // Just update the lines and reorganize
        const reorganized = reorganizeLines(updatedLines);
        setPickingLines(reorganized);
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      alert(t('warehouse.modules.picking.alerts.updateQuantityFailed'));
      await resyncAfterFailedUpdate(lineNo);
    }
  };

  /**
   * Handle serial number picked from SerialPickModal (ItemType=6).
   * Increments QtyPicked by 1 and passes the selected serialNo to the backend.
   */
  const handleSerialPicked = async (serialNo: string) => {
    if (!serialPickLine) return;

    const lineNo = serialPickLine.pListLineNo;
    const newTotalQty = serialPickLine.qtyPicked + 1;

    try {
      const startDateTime = lineStartTimes.get(lineNo);

      await pickingService.updatePickedQuantity(
        pListId, lineNo, newTotalQty, undefined, startDateTime, serialNo
      );

      // Also create OrderSerialNo record for ERP export file
      await pickingService.createSerialNumber(
        pListId, serialPickLine.orderNo, serialPickLine.lineNo, serialNo
      );

      // Close modal
      setShowSerialPickModal(false);
      setSerialPickLine(null);

      // Reload pallets
      await loadPallets();

      // Update local state
      const updatedLines = pickingLines.map(l => {
        if (l.pListLineNo === lineNo) {
          const isFullyPicked = newTotalQty >= l.qty;
          const isPartiallyPicked = newTotalQty > 0 && newTotalQty < l.qty;
          return {
            ...l,
            qtyPicked: newTotalQty,
            isCompleted: newTotalQty > 0,
            isPartiallyPicked,
            isFullyPicked,
            palletId: activePallet?.sscc
          };
        }
        return l;
      });

      const reorganized = reorganizeLines(updatedLines);
      setPickingLines(reorganized);
      // Delplukket: aktiv linje følger linjen etter reorganisering; ferdig: neste uplukkede (index 0)
      const reorganizedIndex = reorganized.findIndex(l => l.pListLineNo === lineNo);
      setActiveLineIndex(reorganizedIndex !== -1 && !reorganized[reorganizedIndex].isFullyPicked ? reorganizedIndex : firstPickableIndex(reorganized));
    } catch (error: any) {
      console.error('Error picking serial:', error);
      alert(error.message || t('warehouse.modules.picking.alerts.serialPickFailed'));
    }
  };

  /**
   * D-pak: registrer plukket antall på linjen.
   *
   * Går bevisst UTENOM handleUpdateQuantity. Den oppretter i tillegg en OrderSSCC-rad mot
   * den aktive pallen for å betjene den manuelle pall-flyten — i D-pak-flyten har vi
   * allerede opprettet raden for den nye pallen, så resultatet ville blitt to rader og
   * dobbelttelt antall. Mønsteret her speiler handleBatchPicked.
   */
  const applyDPakPickedQuantity = async (line: ExtendedPickingLine, addedQuantity: number) => {
    const lineNo = line.pListLineNo;
    const newTotalQty = line.qtyPicked + addedQuantity;
    const startDateTime = lineStartTimes.get(lineNo);

    // 36334078: D-pak-flyten er per definisjon skann-drevet
    await pickingService.updatePickedQuantity(pListId, lineNo, newTotalQty, undefined, startDateTime, undefined, 'Scanned');

    const updatedLines = pickingLines.map(l => {
      if (l.pListLineNo !== lineNo) return l;
      const isFullyPicked = newTotalQty >= l.qty;
      return {
        ...l,
        qtyPicked: newTotalQty,
        isCompleted: newTotalQty > 0,
        isPartiallyPicked: newTotalQty > 0 && newTotalQty < l.qty,
        isFullyPicked,
      };
    });

    setPickingLines(reorganizeLines(updatedLines));
    setActiveLineIndex(0);
  };

  // ─── D-pak-skann → automatisk pall ───
  // Kalles her (ikke øverst) fordi den avhenger av findLineByItemWithResolve, som defineres
  // lenger opp i komponentkroppen. Hook-rekkefølgen er stabil, så plasseringen er trygg.
  // Hooken orkestrerer kun eksisterende byggeklosser og er inaktiv når innstillingen er av.
  const dpak = useDPakScan({
    enabled: autoCreatePalletOnDPakScan,
    pListId,
    findLineByItemWithResolve,
    applyPickedQuantity: applyDPakPickedQuantity,
    loadPallets,
    ssccReportId: selectedSsccReportId,
  });

  /**
   * Handle batch picked from BatchPickModal (ItemType=4/5).
   * Updates QtyPicked by the selected quantity and registers the batch via OrderBatchNo.
   */
  const handleBatchPicked = async (batchNo: string, quantity: number, expiryDate?: string | null) => {
    if (!batchPickLine) return;

    const lineNo = batchPickLine.pListLineNo;
    const newTotalQty = batchPickLine.qtyPicked + quantity;

    try {
      const startDateTime = lineStartTimes.get(lineNo);

      await pickingService.updatePickedQuantity(
        pListId, lineNo, newTotalQty, undefined, startDateTime
      );

      // Create OrderBatchNo record for ERP export file
      await pickingService.createBatchNumber(
        pListId,
        batchPickLine.orderNo,
        batchPickLine.lineNo,
        batchPickLine.pListLineNo,
        batchNo,
        quantity,
        undefined,
        expiryDate ?? null
      );

      // Close modal
      setShowBatchPickModal(false);
      setBatchPickLine(null);

      // Reload pallets
      await loadPallets();

      // Update local state
      const updatedLines = pickingLines.map(l => {
        if (l.pListLineNo === lineNo) {
          const isFullyPicked = newTotalQty >= l.qty;
          const isPartiallyPicked = newTotalQty > 0 && newTotalQty < l.qty;
          return {
            ...l,
            qtyPicked: newTotalQty,
            isCompleted: newTotalQty > 0,
            isPartiallyPicked,
            isFullyPicked,
            palletId: activePallet?.sscc
          };
        }
        return l;
      });

      const reorganized = reorganizeLines(updatedLines);
      setPickingLines(reorganized);
      // Delplukket: aktiv linje følger linjen etter reorganisering; ferdig: neste uplukkede (index 0)
      const reorganizedIndex = reorganized.findIndex(l => l.pListLineNo === lineNo);
      setActiveLineIndex(reorganizedIndex !== -1 && !reorganized[reorganizedIndex].isFullyPicked ? reorganizedIndex : firstPickableIndex(reorganized));
    } catch (error: any) {
      console.error('Error picking batch:', error);
      alert(error.message || t('warehouse.modules.picking.alerts.batchPickFailed'));
    }
  };

  const createNewPallet = async (ssccCode?: string, gs1Data?: GS1ScanData) => {
    // SCENARIO 1: Pre-filled pallet (GS1 with item data)
    if (gs1Data && (gs1Data.gtin || gs1Data.itemNo)) {
      const matchedLine = await findLineByItemWithResolve(gs1Data.gtin || gs1Data.itemNo!);

      if (!matchedLine) {
        alert(t('warehouse.modules.picking.alerts.noLineMatchesGtin', { 0: gs1Data.gtin || gs1Data.itemNo }));
        return;
      }

      // Generate SSCC if not provided: bruk firmaets SystemSetupSSCC-konfig,
      // fall tilbake til lokal tidsstempel-basert pall-ID hvis ikke konfigurert.
      const finalSSCC = ssccCode || (await pickingService.generateSscc()) || `PALL-${Date.now()}`;

      try {
        // Create OrderSSCC with REAL data from GS1
        const orderSSCCRequest: CreateOrderSSCCRequest = {
          companyNo: pickingDetails!.header.companyNo,
          pListID: pListId,
          orderNo: matchedLine.orderNo,
          lineNo: matchedLine.lineNo,
          sscc: finalSSCC,
          gtin: gs1Data.gtin || undefined,
          quantity: gs1Data.quantity || matchedLine.qty,
          ssccLevel: 1,
          itemBatchNo: gs1Data.batchNo || '',
          itemBatchExpDate: gs1Data.expiryDate || null,
          locationName: matchedLine.binCode,
          transitPackagingID: palletCarrierId ?? undefined
        };

        await pickingService.createOrUpdateSSCC(pListId, orderSSCCRequest);

        // Update picked quantity — pallraden er registrert over, så den skal ikke legges på pall igjen
        await handleUpdateQuantity(
          matchedLine.pListLineNo,
          matchedLine.qtyPicked + (gs1Data.quantity || matchedLine.qty),
          true
        );

        // Reload from database
        await loadPallets();
        setActivePalletSSCC(finalSSCC);
        alert(t('warehouse.modules.picking.alerts.palletCreatedWithItem', { 0: finalSSCC }));
        return;
      } catch (error) {
        console.error('Error creating pre-filled pallet:', error);
        alert(t('warehouse.modules.picking.alerts.createPalletFailed'));
        return;
      }
    }

    // SCENARIO 2: Empty pallet (no item data)
    // ✅ NEW: Create frontend-only empty pallet (NO DATABASE CALL!)
    // Generer ekte SSCC når operatøren ikke skanner/taster en kode selv;
    // fall tilbake til lokal pall-ID hvis SSCC ikke er konfigurert.
    const emptySscc = ssccCode || (await pickingService.generateSscc()) || `PALL-${Date.now()}`;
    const carrier = palletCarriers.find(c => c.transitPackagingID === palletCarrierId);
    createEmptyPallet(emptySscc, carrier ? { id: carrier.transitPackagingID, descr: carrier.transitPackagingDescr } : null);
    console.log('✅ Empty pallet created (frontend-only, no database)');
  };

  const setActivePallet = (sscc: string) => {
    setActivePalletSSCC(sscc);
  };

  // ─── Bytt lastbærertype (TW 36396318) — kun før avslutning; pallpanelet vises bare under plukk ───
  const changePalletCarrier = async (sscc: string, transitPackagingID: number | null) => {
    const pallet = pallets.find(p => p.sscc === sscc);
    if (!pallet) return;
    const carrier = palletCarriers.find(c => c.transitPackagingID === transitPackagingID) ?? null;

    if (isEmptyPallet(pallet)) {
      setEmptyPalletCarrier(sscc, carrier ? { id: carrier.transitPackagingID, descr: carrier.transitPackagingDescr } : null);
      return;
    }
    try {
      await pickingService.setPalletTransitPackaging(pListId, sscc, transitPackagingID);
      await loadPallets();
    } catch (err: any) {
      console.error('Kunne ikke bytte lastbærertype:', err);
      alert(err?.message || t('warehouse.modules.picking.pallets.changeCarrierFailed', {}, 'Kunne ikke bytte lastbærertype.'));
    }
  };

  // ─── Fordel uplasserte varer under plukk (TW 36396316) ───
  // Uplassert = plukket minus det som ligger på en pall (samme linjematch som chipsene).
  const placedQtyByLine: Record<number, number> = {};
  pickingLines.forEach(l => {
    placedQtyByLine[l.pListLineNo] = getPalletAllocationsForLine(palletSummaries, l, pickingLines)
      .reduce((sum, a) => sum + a.quantity, 0);
  });
  const unplacedQty = pickingLines.reduce(
    (sum, l) => sum + Math.max(0, (l.qtyPicked ?? 0) - (placedQtyByLine[l.pListLineNo] ?? 0)), 0);
  // Fordel-knappen vises så snart noe er plukket (TW 36397706), ikke bare når noe er uplassert.
  const hasPickedLines = pickingLines.some(l => (l.qtyPicked ?? 0) > 0);

  const carrierFallbackName = t('warehouse.modules.picking.pallets.carrier', {}, 'Lastbærer');
  // Eksisterende paller seedes med dagens plassering per plukklinje (TW 36397752), slik at
  // fordelingsmodalen viser innholdet og lar operatøren flytte/frigi.
  const existingDistributionCarriers: ExistingDistributionCarrier[] = palletSummaries.map(p => ({
    sscc: p.sscc,
    name: getCarrierDisplayName(palletSummaries, p.sscc, carrierFallbackName),
    transitPackagingID: p.transitPackagingID ?? 0,
    transitPackagingDescr: p.transitPackagingDescr || carrierFallbackName,
    lines: pickingLines
      .map(l => ({
        pListLineNo: l.pListLineNo,
        qty: getPalletAllocationsForLine(palletSummaries, l, pickingLines)
          .filter(a => a.sscc === p.sscc)
          .reduce((sum, a) => sum + a.quantity, 0)
      }))
      .filter(l => l.qty > 0)
  }));

  /**
   * Reduserer det som ligger av én plukklinje på én bestemt pall (TW 36397752): nyeste rad
   * først, rad slettes ved 0, bruttovekt skaleres. Radene matches på ordrelinjen (palletHelpers)
   * og leses ferskt — søsken-plukklinjer deler rad, så en liste lest før lagringen kan være utdatert.
   */
  const reduceRowsOnPallet = async (
    sscc: string,
    line: ExtendedPickingLine,
    amount: number
  ) => {
    if (amount <= 0) return;
    const candidates = (await pickingService.getOrderSSCCs(pListId))
      .filter(r => r.sscc === sscc && isPalletRowForLine(r, line))
      .sort((a, b) => b.id - a.id);
    let remaining = amount;
    for (const row of candidates) {
      if (remaining <= 0) break;
      const take = Math.min(row.quantity, remaining);
      remaining -= take;
      const newQty = row.quantity - take;
      if (newQty <= 0) {
        await pickingService.deleteSSCC(pListId, row.id);
      } else {
        const scaledWeight = row.quantity > 0 && row.grossWeight
          ? Math.round((row.grossWeight * newQty / row.quantity) * 1000) / 1000
          : row.grossWeight ?? 0;
        await pickingService.updateOrderSSCC(pListId, row.id, {
          quantity: newQty,
          gtin: row.gtin ?? null,
          itemBatchNo: row.itemBatchNo ?? null,
          itemBatchExpDate: row.itemBatchExpDate ?? null,
          stackingWeight: row.stackingWeight ?? null,
          grossWeight: scaledWeight
        });
      }
    }
  };

  /**
   * Lagrer fordelingen under plukk som differanser mot dagens plassering (TW 36396316 +
   * 36397752): eksisterende paller får rader lagt til / redusert / fjernet per linje, nye
   * bærere blir ekte paller. Ingen kollilinjer — de lages ved avslutning som før.
   * Lokasjonen settes av backend (TW 36396315).
   */
  const handlePickDistributionConfirm = async (result: CarrierDistributionResult[]) => {
    if (result.length === 0) return;
    setPickDistributionBusy(true);
    try {
      const addToPallet = async (sscc: string, line: ExtendedPickingLine, qty: number, transitPackagingID: number | null | undefined) => {
        // Backend slår opp (pall, ordre, linje, batch) og overskriver Quantity — legg derfor til
        // eksisterende rad. Leses ferskt: søsken-plukklinjer skriver til samme rad etter hverandre.
        const existingRowQty = await getExistingPalletRowQty(sscc, line, '');
        await pickingService.createOrUpdateSSCC(pListId, {
          companyNo: '',
          pListID: pListId,
          orderNo: Number(line.orderNo),
          // Ordrelinjens LineNo — eksporten, GTIN/vekt og innholdsetiketten joiner mot OrderLine
          lineNo: palletRowLineNo(line),
          sscc,
          quantity: existingRowQty + qty,
          ssccLevel: 1,
          itemBatchNo: '',
          itemBatchExpDate: null,
          stackingWeight: 0,
          grossWeight: 0,
          locationName: line.binCode || line.location,
          transitPackagingID: transitPackagingID || undefined
        });
      };

      for (const carrier of result) {
        if (carrier.sscc) {
          // Eksisterende pall: differanse per linje mot det som lå der da dialogen åpnet
          const seeded = existingDistributionCarriers.find(c => c.sscc === carrier.sscc)?.lines ?? [];
          const lineNos = Array.from(new Set<number>([...seeded.map(l => l.pListLineNo), ...carrier.lines.map(l => l.pListLineNo)]));
          for (const pListLineNo of lineNos) {
            const pickLine = pickingLines.find(l => l.pListLineNo === pListLineNo);
            if (!pickLine) continue;
            const target = carrier.lines.find(l => l.pListLineNo === pListLineNo)?.qty ?? 0;
            const original = seeded.find(l => l.pListLineNo === pListLineNo)?.qty ?? 0;
            const delta = target - original;
            if (delta > 0) {
              await addToPallet(carrier.sscc, pickLine, delta, carrierIdForPallet(carrier.sscc));
            } else if (delta < 0) {
              await reduceRowsOnPallet(carrier.sscc, pickLine, -delta);
            }
          }
          continue;
        }

        // Ny bærer → ny pall
        let generated: string | null = null;
        try { generated = await pickingService.generateSscc(); } catch { generated = null; }
        const sscc = generated || `PALL-${Date.now()}`;
        for (const line of carrier.lines) {
          const pickLine = pickingLines.find(l => l.pListLineNo === line.pListLineNo);
          if (!pickLine) continue;
          await addToPallet(sscc, pickLine, line.qty, carrier.transitPackagingID);
        }
      }
      await loadPallets();
      setShowPickDistribution(false);
    } catch (err: any) {
      console.error('Fordeling under plukk feilet:', err);
      alert(err?.message || t('warehouse.modules.picking.pallets.distributeFailed', {}, 'Kunne ikke lagre fordelingen. Prøv igjen.'));
      await loadPallets();
    } finally {
      setPickDistributionBusy(false);
    }
  };

  const deletePallet = async (sscc: string) => {
    try {
      const palletToDelete = pallets.find(p => p.sscc === sscc);
      if (!palletToDelete) return;

      // Check if empty frontend-only pallet
      if (isEmptyPallet(palletToDelete)) {
        console.log('🗑️ Deleting empty frontend-only pallet:', sscc);
        deleteEmptyPallet(sscc);
        console.log('✅ Empty pallet deleted (no database call)');
        return;
      }

      // Database pallet - delete all OrderSSCC records
      const records = await pickingService.getSSCCContents(sscc);

      // Rull tilbake plukket antall for hver linje pallen bidro til. Uten dette blir
      // QtyPicked stående etter sletting, og linja ser plukket ut uten pall-kobling.
      // Gated bak D-pak-innstillingen i første omgang: for tenanter uten auto-pall er
      // dagens oppførsel uendret, slik at ingen eksisterende kunde overraskes (AP5).
      if (autoCreatePalletOnDPakScan) {
        // Array, ikke Map — prosjektets TS-target tillater ikke Map-iterasjon.
        const deductions: { pListLineNo: number; quantity: number }[] = [];

        // Hver plukklinjes andel av pallen. Pallradene eies av ordrelinjen; søsken-plukklinjer
        // som deler ordrelinje får sin del via fordelingen i palletHelpers.
        for (const l of pickingLines) {
          const quantity = getPalletAllocationsForLine(palletSummaries, l, pickingLines)
            .filter(a => a.sscc === sscc)
            .reduce((sum, a) => sum + a.quantity, 0);
          if (quantity > 0) {
            deductions.push({ pListLineNo: l.pListLineNo, quantity });
          }
        }

        for (const deduction of deductions) {
          const affected = pickingLines.find(l => l.pListLineNo === deduction.pListLineNo);
          if (!affected) continue;
          const newTotal = Math.max(0, affected.qtyPicked - deduction.quantity);
          await pickingService.updatePickedQuantity(
            pListId, deduction.pListLineNo, newTotal, undefined,
            lineStartTimes.get(deduction.pListLineNo)
          );
        }
      }

      for (const record of records) {
        await pickingService.deleteSSCC(pListId, record.id);
      }

      // Reload pallets og plukklinjer (QtyPicked kan ha blitt rullet tilbake over)
      await loadPallets();
      if (autoCreatePalletOnDPakScan) {
        await loadPickingDetails();
      }

      console.log('✅ Database pallet deleted:', sscc);
    } catch (error) {
      console.error('Error deleting pallet:', error);
      alert(t('warehouse.modules.picking.alerts.deletePalletFailed'));
    }
  };

  const handleLocationEdit = async (lineNo: number, location: string) => {
    // Guard against duplicate calls - only process if this line is currently being edited
    if (editingLocation !== lineNo) {
      return;
    }

    // Immediately reset editing state to prevent duplicate calls
    setEditingLocation(null);
    setNewLocation('');

    const line = pickingLines.find(l => l.pListLineNo === lineNo);
    if (!line) {
      return;
    }

    // TW 36453038: backend avgjør. UpdateBinCodeAsync sjekker at lokasjonen finnes i linjens lager,
    // summerer saldo over alle ItemBins-rader (også serie/batch), trekker fra reservasjoner for andre
    // ordrer og hopper over alt for vare-GO. Den tidligere forhåndssjekken her leste bare første
    // ItemBins-rad uten lagerfilter, og avviste serie-/batchlinjer på 2+ stk som backend godtar.
    try {
      await pickingService.updateBinCode(pListId, lineNo, location);

      // Update local state
      const updatedLines = pickingLines.map(l =>
        l.pListLineNo === lineNo ? { ...l, location, binCode: location } : l
      );
      setPickingLines(updatedLines);
    } catch (error) {
      const errorCode = error instanceof ApiError ? error.errorCode : undefined;

      // For AW-shortage-linjer ([X]-BinCode) tilbyr vi shortage-resolution-flyten via
      // overstyrings-modalen — operatør kan visuelt bekrefte antall + velge ReasonCode,
      // og Compass korrigerer saldo internt (ikke til ERP) før plukket fortsetter.
      if (errorCode === 'INSUFFICIENT_STOCK' && line.binCode === '[X]' && isAdvancedWarehouse) {
        const lineIndex = pickingLines.indexOf(line);
        setCurrentLineIndex(lineIndex);
        setOverrideLocation(location);
        setShowLocationOverride(true);
        return;
      }

      if (errorCode === 'BIN_NOT_FOUND') {
        alert(t('warehouse.modules.picking.alerts.locationNotFound', { 0: location }));
        return;
      }

      if (errorCode === 'INSUFFICIENT_STOCK' && error instanceof ApiError) {
        const availableQty = Number(error.details?.availableQuantity ?? 0);
        const requiredQty = Number(error.details?.requiredQuantity ?? line.qty - line.qtyPicked);
        alert(t('warehouse.modules.picking.alerts.insufficientInventory', { 0: line.itemNo, 1: availableQty, 2: location, 3: requiredQty }));
        return;
      }

      // Øvrige avvisninger (reservert for andre ordrer, operatørens egen lokasjon, …): vis backend sin melding.
      console.error('Error changing location:', error);
      alert(error instanceof ApiError && error.message
        ? error.message
        : t('warehouse.modules.picking.alerts.validateLocationFailed'));
    }
  };

  /**
   * Trekk `amount` fra OrderSSCC-radene til én linje (TW 36393700): aktiv pall først, deretter
   * nyeste rad. Rader som når 0 slettes; bruttovekt skaleres med antallet.
   */
  const reducePalletRowsForLine = async (line: ExtendedPickingLine, amount: number) => {
    if (amount <= 0) return;
    const rows = (await pickingService.getOrderSSCCs(pListId))
      .filter(r => isPalletRowForLine(r, line))
      .sort((a, b) => {
        const aActive = activePallet && a.sscc === activePallet.sscc ? 0 : 1;
        const bActive = activePallet && b.sscc === activePallet.sscc ? 0 : 1;
        return aActive - bActive || b.id - a.id;
      });

    let remaining = amount;
    for (const row of rows) {
      if (remaining <= 0) break;
      const take = Math.min(row.quantity, remaining);
      remaining -= take;
      const newQty = row.quantity - take;
      if (newQty <= 0) {
        await pickingService.deleteSSCC(pListId, row.id);
      } else {
        const scaledWeight = row.quantity > 0 && row.grossWeight
          ? Math.round((row.grossWeight * newQty / row.quantity) * 1000) / 1000
          : row.grossWeight ?? 0;
        await pickingService.updateOrderSSCC(pListId, row.id, {
          quantity: newQty,
          gtin: row.gtin ?? null,
          itemBatchNo: row.itemBatchNo ?? null,
          itemBatchExpDate: row.itemBatchExpDate ?? null,
          stackingWeight: row.stackingWeight ?? null,
          grossWeight: scaledWeight
        });
      }
    }
  };

  const handleQuantityEdit = async (lineNo: number, newQty: number) => {
    const line = pickingLines.find(l => l.pListLineNo === lineNo);
    if (!line) return;

    // Overplukk-sperre — oppheves av ERP-flagget / manuell linje (TW 36401594, bizClick-regel)
    if (newQty > line.qty && !isOverPickAllowed(line)) {
      alert(t('warehouse.modules.picking.alerts.quantityExceedsOrdered', { 0: line.qty }));
      setEditingQuantity(null);
      setNewQuantity('');
      return;
    }

    try {
      // Get StartDateTime for this line from state
      const startDateTime = lineStartTimes.get(lineNo);

      // 36334078: blyant-redigering av antall er manuell inntasting
      await pickingService.updatePickedQuantity(pListId, lineNo, newQty, undefined, startDateTime, undefined, 'Manual');

      // TW 36393700: pall-kobling også på blyanten. Økning → OrderSSCC-rad mot aktiv pall (som skann);
      // reduksjon → trekk fra linjas pallrad(er), aktiv pall først, slett ved 0.
      // Tellekontroll og serie/batch forblir bevisst utenom denne stien.
      const delta = newQty - line.qtyPicked;
      try {
        if (delta > 0 && activePallet) {
          // Backend overskriver raden — legg til det som allerede ligger der
          const existingRowQty = await getExistingPalletRowQty(activePallet.sscc, line, '');
          const ssccRequest: CreateOrderSSCCRequest = {
            companyNo: '',
            pListID: pListId,
            orderNo: line.orderNo,
            lineNo: palletRowLineNo(line), // ordrelinjens LineNo — ikke pListLineNo
            sscc: activePallet.sscc,
            quantity: existingRowQty + delta,
            ssccLevel: 1,
            itemBatchNo: '',
            itemBatchExpDate: null,
            stackingWeight: 0,
            grossWeight: 0, // backend beregner fra OrderLine.Weight
            locationName: line.binCode || line.location,
            transitPackagingID: carrierIdForPallet(activePallet.sscc)
          };
          await pickingService.createOrUpdateSSCC(pListId, ssccRequest);
        } else if (delta < 0) {
          await reducePalletRowsForLine(line, -delta);
        }
      } catch (palletErr) {
        // Plukket antall er allerede lagret — pallavviket meldes, men blokkerer ikke.
        console.warn('Pall-justering etter antallsendring feilet:', palletErr);
      }

      const updatedLines = pickingLines.map(l => {
        if (l.pListLineNo === lineNo) {
          const isFullyPicked = newQty >= l.qty;
          const isPartiallyPicked = newQty > 0 && newQty < l.qty;
          
          return {
            ...l,
            qtyPicked: newQty,
            isCompleted: newQty > 0,
            isPartiallyPicked,
            isFullyPicked
          };
        }
        return l;
      });

      // Reorganize lines. Delplukket/nullet: linjen forblir aktiv der den havner; ferdig: neste uplukkede (index 0)
      const reorganized = reorganizeLines(updatedLines);
      setPickingLines(reorganized);
      const reorganizedIndex = reorganized.findIndex(l => l.pListLineNo === lineNo);
      setActiveLineIndex(reorganizedIndex !== -1 && !reorganized[reorganizedIndex].isFullyPicked ? reorganizedIndex : firstPickableIndex(reorganized));

      setEditingQuantity(null);
      setNewQuantity('');

      // Reload pallets from database to get updated data
      await loadPallets();
    } catch (error) {
      console.error('Error updating quantity:', error);
      alert(t('warehouse.modules.picking.alerts.updateQuantityFailed'));
      setEditingQuantity(null);
      setNewQuantity('');
      await resyncAfterFailedUpdate(lineNo);
    }
  };

  const handleUndoPicking = async (lineNo: number) => {
    // Find the line to get item info for the confirmation message
    const line = pickingLines.find(l => l.pListLineNo === lineNo);
    const lineInfo = line
      ? `${line.itemNo} - ${line.itemDescription || t('common.labels.unknown')} (${t('common.fields.quantity')}: ${line.qtyPicked})`
      : t('warehouse.modules.picking.alerts.lineLabel', { 0: lineNo });

    // Show confirmation dialog
    const confirmed = window.confirm(
      t('warehouse.modules.picking.alerts.undoPickConfirm', { 0: lineInfo })
    );

    if (!confirmed) {
      return; // User cancelled
    }

    try {
      // Get StartDateTime for this line from state (even though resetting to 0)
      const startDateTime = lineStartTimes.get(lineNo);

      // Søsken-plukklinjer deler ordrelinjens pallrader: da trekkes bare denne linjas andel
      // (beregnes før plukket nulles). Uten søsken slettes alle radene som før.
      const hasSiblings = !!line && getSiblingLines(pickingLines, line).length > 1;
      const siblingShare = hasSiblings && line
        ? getPalletAllocationsForLine(palletSummaries, line, pickingLines).reduce((sum, a) => sum + a.quantity, 0)
        : 0;

      // Update picked quantity to 0 in PListLine - this triggers backend reversal logic
      await pickingService.updatePickedQuantity(pListId, lineNo, 0, undefined, startDateTime);

      if (hasSiblings && line) {
        await reducePalletRowsForLine(line, siblingShare);
      } else {
        // Get and delete all SSCC records for this line
        const allSSCCs = await pickingService.getOrderSSCCs(pListId);
        // Kun ordrelinjens rader — filter på LineNo alene traff også andre ordrer/linjer
        const lineSSCCs = line ? allSSCCs.filter(sscc => isPalletRowForLine(sscc, line)) : [];

        for (const sscc of lineSSCCs) {
          await pickingService.deleteSSCC(pListId, sscc.id);
        }
      }

      // Delete all OrderSerialNo records for this line (used in ERP export)
      if (line) {
        await pickingService.deleteSerialNumbersByLine(pListId, line.orderNo, line.lineNo);
      }

      // Reload pallets from database
      await loadPallets();
      
      // Update local state
      const updatedLines = pickingLines.map(l =>
        l.pListLineNo === lineNo
          ? {
              ...l,
              qtyPicked: 0,
              isCompleted: false,
              isPartiallyPicked: false,
              isFullyPicked: false,
              palletId: undefined
            }
          : l
      );

      // Reorganize. Nullet linje blir uplukket (havner øverst) — re-finn den så den forblir aktiv.
      const reorganized = reorganizeLines(updatedLines);
      setPickingLines(reorganized);
      const reorganizedIndex = reorganized.findIndex(l => l.pListLineNo === lineNo);
      setActiveLineIndex(reorganizedIndex !== -1 ? reorganizedIndex : firstPickableIndex(reorganized));
      setCurrentLineIndex(reorganizedIndex !== -1 ? reorganizedIndex : firstPickableIndex(reorganized));

      console.log('Undid picking for line:', lineNo);
    } catch (error) {
      console.error('Error undoing pick:', error);
      alert(t('warehouse.modules.picking.alerts.undoPickFailed'));
    }
  };

  /**
   * Endre antall på en tilleggslinje («Legg til vare») — TW 36393701. Backend setter bestilt
   * antall, lar plukket følge via plukkflyten og justerer pallraden. Kun status < 50.
   */
  const openEditAdditionalLine = (lineNo: number) => {
    const line = pickingLines.find(l => l.pListLineNo === lineNo);
    if (!line) return;
    setEditAdditionalLine({ lineNo, qty: String(line.qty) });
  };

  const handleConfirmEditAdditionalLine = async () => {
    if (!editAdditionalLine) return;
    const qty = parseInt(editAdditionalLine.qty, 10);
    if (!qty || qty < 1) {
      alert(t('warehouse.modules.picking.alerts.additionalLineQuantityMin', {}, 'Antall må være minst 1. Bruk slett for å fjerne linja.'));
      return;
    }
    const lineNo = editAdditionalLine.lineNo;
    setEditAdditionalLine(null);
    try {
      const result = await pickingService.setAdditionalLineQuantity(pListId, lineNo, qty);
      if (!result.success) {
        alert(result.message || t('warehouse.modules.picking.alerts.editAdditionalLineFailed', {}, 'Kunne ikke endre antall på tilleggslinjen.'));
      }
      await loadPickingDetails();
      await loadPallets();
    } catch (error) {
      console.error('Error updating additional line quantity:', error);
      alert(t('warehouse.modules.picking.alerts.editAdditionalLineFailed', {}, 'Kunne ikke endre antall på tilleggslinjen.'));
    }
  };

  /**
   * Slett en manuelt tillagt linje («Legg til vare») — TW 36391640. Backend reverserer lager,
   * fjerner SSCC/serie/batch og sletter PListLine + OrderLine; vi laster lista på nytt etterpå.
   */
  const handleDeleteAdditionalLine = async (lineNo: number) => {
    const line = pickingLines.find(l => l.pListLineNo === lineNo);
    const lineInfo = line
      ? `${line.itemNo} - ${line.itemDescription || t('common.labels.unknown')} (${t('common.fields.quantity')}: ${line.qtyPicked}/${line.qty})`
      : t('warehouse.modules.picking.alerts.lineLabel', { 0: lineNo });

    const confirmed = window.confirm(
      t('warehouse.modules.picking.alerts.deleteAdditionalLineConfirm', { 0: lineInfo }, 'Slette tilleggslinjen {0}?\n\nPlukket antall reverseres og linja fjernes fra ordren.')
    );
    if (!confirmed) return;

    try {
      await pickingService.deleteAdditionalLine(pListId, lineNo);
      await loadPickingDetails();
      await loadPallets();
    } catch (error) {
      console.error('Error deleting additional line:', error);
      alert(t('warehouse.modules.picking.alerts.deleteAdditionalLineFailed', {}, 'Kunne ikke slette tilleggslinjen.'));
    }
  };

  // Print SSCC label for the active pallet
  const handlePrintActivePalletSSCC = async () => {
    if (!activePallet || !user?.companyNo) return;

    setPrintingActivePalletSSCC(true);
    try {
      // Find the pallet summary to get item details
      const palletSummary = pallets.find(p => p.sscc === activePallet.sscc);

      const result = await printingService.printSSCCLabel(user.companyNo, {
        sscc: activePallet.sscc,
        pListId,
        // Bruk valgt SSCC-rapport (ReportType 50); ellers faller backend tilbake til innebygd etikett.
        reportId: selectedSsccReportId,
        quantity: palletSummary?.totalQuantity || undefined,
        labelType: SSCCLabelType.StandardPallet,
        gtin: palletSummary?.items?.[0]?.gtin || undefined,
        batchNo: palletSummary?.items?.[0]?.itemBatchNo || undefined,
        expiryDate: palletSummary?.items?.[0]?.itemBatchExpDate || undefined
      });

      if (!result.success) {
        alert(result.message || t('warehouse.modules.picking.alerts.ssccPrintFailed'));
      }
    } catch (err: any) {
      console.error('Failed to print active pallet SSCC label:', err);
      alert(err.message || t('warehouse.modules.picking.alerts.ssccPrintFailed'));
    } finally {
      setPrintingActivePalletSSCC(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 animate-spin border-2 border-blue-500 border-t-transparent rounded-full" />
        <span className="ml-3 text-gray-600">{t('warehouse.modules.picking.details.loadingDetails')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          onClick={() => loadPickingDetails()}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          {t('common.buttons.tryAgain')}
        </button>
      </div>
    );
  }

  if (!pickingDetails) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">{t('warehouse.modules.picking.details.pickingListNotFound')}</p>
      </div>
    );
  }

  const headerOrderNos = splitOrderNoERPs(pickingDetails.header.orderNoERPs);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile/Enterprise scanner support (Zebra, etc.) - invisible input that captures scanner data */}
      <MobileBarcodeCaptureInput
        enabled={barcodeScannerEnabled && !isAnyModalOpen}
        onScan={(barcode) => {
          console.log('📱 Mobile scanner detected barcode:', barcode);

          // In location-scan-only mode, treat scan as location barcode
          if (isLocationScanOnlyMode && activeLine) {
            const cleanedBarcode = barcode.replace(/[~§]+$/g, '').trim();
            handleLocationOnlyScan(cleanedBarcode);
            return;
          }

          processGlobalBarcode(barcode);
        }}
      />

      {/* Header - Mobile optimized with sticky positioning */}
      <div className="bg-white border-b border-gray-200 p-2 sm:p-4 sticky top-0 z-20">
        <div className="flex items-center justify-between gap-2 max-w-6xl mx-auto">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-shrink-1">
            <BackButton onClick={onBack} />
            {/* Flere ordrer: første ordrenummer + «+N ordrer» (trykk viser alle) — samme som plukkortet (TW 36416444) */}
            <h1 className="text-base sm:text-xl font-semibold truncate" title={t('warehouse.modules.picking.details.pickNumber', { 0: pickingDetails.header.pListID })}>
              {headerOrderNos.length > 0
                ? t('warehouse.modules.picking.details.orderNumber', { 0: headerOrderNos[0] })
                : t('warehouse.modules.picking.details.pickNumber', { 0: pickingDetails.header.pListID })}
            </h1>
            <OrderListBadge
              orderNoERPs={headerOrderNos}
              companyNo={pickingDetails.header.companyNo}
              pListId={pickingDetails.header.pListID}
              className="-ml-1 sm:-ml-2"
            />
          </div>

          {/* Action buttons - horizontally scrollable on narrow screens */}
          <div className="flex items-center gap-0.5 sm:gap-2 overflow-x-auto flex-shrink-0 -mr-2 sm:mr-0 pl-1">
            <button
              onClick={() => setShowPickingPrintModal(true)}
              className={`flex items-center justify-center ${headerBtnSize} text-blue-600 hover:bg-blue-50 rounded-lg transition-colors`}
              title={t('warehouse.modules.picking.details.printLabelsTooltip')}
            >
              <Printer size={largeText ? 24 : 18} className="sm:hidden" />
              <Printer size={largeText ? 24 : 20} className="hidden sm:block" />
            </button>
            {activePallet && (
              <button
                onClick={handlePrintActivePalletSSCC}
                disabled={printingActivePalletSSCC}
                className={`flex items-center justify-center ${headerBtnSize} text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50`}
                title={t('warehouse.modules.picking.details.printSsccTooltip', { 0: activePallet.sscc })}
              >
                {printingActivePalletSSCC ? (
                  <div className="w-4 h-4 sm:w-5 sm:h-5 animate-spin border-2 border-green-600 border-t-transparent rounded-full" />
                ) : (
                  <>
                    <ScanBarcode size={largeText ? 24 : 18} className="sm:hidden" />
                    <ScanBarcode size={largeText ? 24 : 20} className="hidden sm:block" />
                  </>
                )}
              </button>
            )}
            <button
              onClick={() => { setPrintKolliCount('1'); setShowPrintKolliModal(true); }}
              className={`flex items-center justify-center ${headerBtnSize} text-gray-600 hover:bg-gray-100 rounded-lg transition-colors`}
              title={t('warehouse.modules.picking.details.printKolliLabelTooltip', {}, 'Skriv ut kolli-etikett')}
            >
              <Tags size={largeText ? 24 : 18} className="sm:hidden" />
              <Tags size={largeText ? 24 : 20} className="hidden sm:block" />
            </button>
            <button
              onClick={() => setShowPalletManager(!showPalletManager)}
              className={`relative flex items-center justify-center ${headerBtnSize} text-gray-600 hover:bg-gray-100 rounded-lg transition-colors`}
            >
              <Package2 size={largeText ? 24 : 18} className="sm:hidden" />
              <Package2 size={largeText ? 24 : 20} className="hidden sm:block" />
              {activePallet && (
                <div className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
              )}
            </button>
            <button
              onClick={() => setShowSignatureModal(true)}
              className={`flex items-center justify-center ${headerBtnSize} text-gray-600 hover:bg-gray-100 rounded-lg transition-colors`}
              title={t('warehouse.modules.picking.details.addSignatureTooltip')}
            >
              <PenTool size={largeText ? 24 : 18} className="sm:hidden" />
              <PenTool size={largeText ? 24 : 20} className="hidden sm:block" />
            </button>
            <MediaCaptureButton
              entityType="PList"
              entityKey={pListId.toString()}
            />
            <button
              onClick={() => setShowAddLineModal(true)}
              className={`flex items-center justify-center ${headerBtnSize} text-green-600 hover:bg-green-50 rounded-lg transition-colors`}
              title={t('warehouse.modules.picking.details.addItemTooltip')}
            >
              <Plus size={largeText ? 24 : 18} className="sm:hidden" />
              <Plus size={largeText ? 24 : 20} className="hidden sm:block" />
            </button>
            <button
              onClick={() => setShowSettingsModal(true)}
              className={`flex items-center justify-center ${headerBtnSize} text-gray-600 hover:bg-gray-100 rounded-lg transition-colors`}
              title={t('warehouse.modules.picking.details.settingsTooltip')}
            >
              <Settings size={largeText ? 24 : 18} className="sm:hidden" />
              <Settings size={largeText ? 24 : 20} className="hidden sm:block" />
            </button>
          </div>
        </div>
      </div>

      {kolliToast && (
        // Øverst, ikke nederst: på nettbrett ble meldingen nederst dekket av nettleserens/
        // systemets bunnlinje og var ikke lesbar
        <div role="status" className="fixed top-20 left-1/2 -translate-x-1/2 z-50 max-w-[90vw] bg-gray-900 text-white text-base font-medium px-5 py-3 rounded-lg shadow-lg flex items-center gap-2">
          <Tags size={18} className="flex-shrink-0" />
          <span>{kolliToast}</span>
        </div>
      )}

      {showKolliDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xs p-4">
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              {t('warehouse.modules.picking.details.kolliDialogTitle', {}, 'Antall internkolli')}
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              {t('warehouse.modules.picking.details.kolliDialogPrompt', {}, 'Hvor mange kolli-etiketter skal skrives ut?')}
            </p>
            <NumericInput
              mode="integer"
              value={kolliCount}
              onChange={(e) => setKolliCount(e.target.value)}
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowKolliDialog(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg min-h-[44px]"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleConfirmKolliDialog}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg min-h-[44px]"
              >
                {t('common.buttons.print', {}, 'Skriv ut')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header-ikon (Tags): velg antall kolli-etiketter (default 1) og bekreft med OK. */}
      {showPrintKolliModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xs p-4">
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              {t('warehouse.modules.picking.details.printKolliModalTitle', {}, 'Skriv ut kolli-etiketter')}
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              {t('warehouse.modules.picking.details.printKolliModalPrompt', {}, 'Hvor mange kolli-etiketter skal skrives ut?')}
            </p>
            <NumericInput
              mode="integer"
              value={printKolliCount}
              onChange={(e) => setPrintKolliCount(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleConfirmPrintKolliModal(); } }}
              onFocus={(e) => e.target.select()}
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowPrintKolliModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg min-h-[44px]"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleConfirmPrintKolliModal}
                disabled={!printKolliCount || parseInt(printKolliCount, 10) < 1}
                className="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 rounded-lg min-h-[44px]"
              >
                {t('common.buttons.ok', {}, 'OK')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TW 36393701: nytt antall på tilleggslinje */}
      {editAdditionalLine && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xs p-4">
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              {t('warehouse.modules.picking.details.editAdditionalLineTitle', {}, 'Endre antall på tilleggslinje')}
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              {t('warehouse.modules.picking.details.editAdditionalLinePrompt', {}, 'Nytt antall (plukket antall følger med):')}
            </p>
            <NumericInput
              mode="integer"
              quantity
              value={editAdditionalLine.qty}
              onChange={(e) => setEditAdditionalLine(prev => prev ? { ...prev, qty: e.target.value } : prev)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleConfirmEditAdditionalLine(); } }}
              onFocus={(e) => e.target.select()}
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setEditAdditionalLine(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg min-h-[44px]"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleConfirmEditAdditionalLine}
                disabled={!editAdditionalLine.qty || parseInt(editAdditionalLine.qty, 10) < 1}
                className="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 rounded-lg min-h-[44px]"
              >
                {t('common.buttons.ok', {}, 'OK')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto p-2 sm:p-4">
        {/* Mobile: Scanner at top, then content. Desktop: Side by side */}
        <div className="flex flex-col lg:grid lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Scanner Column - Full width on mobile, side column on desktop */}
          <div className="order-first lg:order-none">
            <GS1Scanner
              ref={scannerRef}
              onScan={handleScan}
              pallets={palletSummaries}
              activePallet={activePalletSummary}
              onSetActivePallet={setActivePallet}
              onCreatePallet={createNewPallet}
              onCompletePicking={handleCompletePicking}
              onSendToPacking={handleSendToPacking}
              showSendToPacking={showSendToPacking}
              isLocationScanOnlyMode={isLocationScanOnlyMode}
              onLocationScan={handleLocationOnlyScan}
              scanError={scanError}
              onClearScanError={() => setScanError(null)}
              /* Sendes kun i D-pak-modus. Uten proppen beholder GS1Scanner sin helt
                 synkrone skann-sti, uten engang en microtask-forsinkelse. */
              onRawScan={async (raw) =>
                // Allokeringsetikett (ALC-{ResID}) prøves først, deretter D-pak — begge
                // konsumerer kun sine egne formater; alt annet går videre i GS1-løpet.
                tryHandleAllocationScan(raw)
                || (autoCreatePalletOnDPakScan ? await dpak.tryHandleDPakScan(raw) : false)}
            />
          </div>

          {/* System Warnings */}
          {pickingDetails.warnings && pickingDetails.warnings.length > 0 && (
            <div className="lg:col-span-4 mb-2">
              {pickingDetails.warnings.map((warning, i) => (
                <div key={i} className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-start gap-2 mb-1">
                  <span className="text-amber-600 flex-shrink-0 mt-0.5">⚠️</span>
                  <span className="text-sm text-amber-800 font-medium">{warning}</span>
                </div>
              ))}
            </div>
          )}

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Location-scan-only mode indicator */}
            {isLocationScanOnlyMode && activeLine && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-2 mb-2 flex items-center gap-2">
                <MapPin className="text-orange-500 flex-shrink-0" size={16} />
                <span className="text-sm text-orange-700">
                  {t('warehouse.modules.picking.details.scanLocation')}: <strong>{activeLine.location}</strong>
                </span>
              </div>
            )}

            {/* Active Line */}
            <ActiveLine
              largeText={largeText}
              activeLine={activeLineWithStructureCap}
              onScanRequired={() => {}}
              onLocationRequired={() => {}}
              editingLocation={editingLocation}
              editingQuantity={editingQuantity}
              newLocation={newLocation}
              newQuantity={newQuantity}
              onStartLocationEdit={(lineNo, location) => {
                setEditingLocation(lineNo);
                setNewLocation(location);
              }}
              onStartQuantityEdit={(lineNo) => {
                // Antallsredigering går ALLTID via modalen (36334078-testfunn 2026-08-19):
                // inline-boksen kollapset ved re-render (rapid-scan-churn, lokasjonssaldo-
                // lasting m.m.) fordi onBlur committer — modalen er immun og finnes allerede.
                openSetTotalModal(lineNo);
              }}
              onOpenQuantityEditModal={openSetTotalModal}
              onLocationChange={setNewLocation}
              onQuantityChange={setNewQuantity}
              onLocationEdit={handleLocationEdit}
              onQuantityEdit={handleQuantityEdit}
              onShowImage={handleShowImage}
              qtyOnLocation={activeLineStock?.quantity ?? null}
              reservedOnLocation={activeLineStock?.reserved ?? null}
              onShowLocationStock={() => setShowLocationStock(true)}
              deliveryMethodText={pickingDetails?.header?.deliveryMethodDescription ?? null}
              mustGoToPacking={pickingDetails?.hasSiblings === true}
              onStructureCount={activeLineWithStructureCap
                ? () => handleStructureCountRequest(activeLineWithStructureCap.pListLineNo)
                : undefined}
            />

            {/* ItemType=6: Serial-tracked inventory - info banner */}
            {activeLine && activeLine.itemType === 6 && (
              <div className="mb-2 sm:mb-4 p-2 sm:p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="text-sm text-emerald-800 font-medium">
                    {t('warehouse.modules.picking.details.serialControlledItem')}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-emerald-700">
                      {t('warehouse.modules.picking.picked')}: {Math.floor(activeLine.qtyPicked)} / {Math.floor(activeLine.qty)}
                    </span>
                    {activeLine.qtyPicked < activeLine.qty && (
                      <button
                        onClick={() => {
                          setSerialPickLine(activeLine);
                          setShowSerialPickModal(true);
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
                      >
                        {t('warehouse.modules.picking.details.selectSerial')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ItemType=4/5: Batch-tracked inventory - info banner */}
            {activeLine && (activeLine.itemType === 4 || activeLine.itemType === 5) && (
              <div className="mb-2 sm:mb-4 p-2 sm:p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="text-sm text-orange-800 font-medium">
                    {t('warehouse.modules.picking.details.batchControlledItem')}
                    {activeLine.requestedBatchNo && (
                      <span className="ml-2 text-orange-600 font-normal">
                        ({t('warehouse.modules.picking.details.expectedLabel')}: <span className="font-mono">{activeLine.requestedBatchNo}</span>)
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-orange-700">
                      {t('warehouse.modules.picking.picked')}: {activeLine.qtyPicked} / {activeLine.qty}
                    </span>
                    {activeLine.qtyPicked < activeLine.qty && (
                      <button
                        onClick={() => {
                          setBatchPickLine(activeLine);
                          setShowBatchPickModal(true);
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
                      >
                        {t('warehouse.modules.picking.details.selectBatch')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Serial/Batch Number Registration Buttons - show when active line requires it.
                For batchvarer (itemType 1) skjules varselet når batch FAKTISK er registrert —
                enten via OrderBatchNo (manuelt) eller OrderSSCC (D-pak-etiketten). Uten denne
                sjekken sto varselet på gjennom hele plukket selv om batchen var fanget. */}
            {activeLine && (
              activeLine.itemType === 2 ||
              (activeLine.itemType === 1 && (
                !autoCreatePalletOnDPakScan ||
                countRegisteredBatches(palletSummaries, activeLine, activeLine.batchNumbers, pickingLines) === 0
              ))
            ) && (
              <div className="mb-2 sm:mb-4 p-2 sm:p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="text-sm text-amber-800 font-medium">
                    {t('warehouse.modules.picking.details.requiresRegistrationOf')}{' '}
                    {activeLine.itemType === 2
                      ? t('warehouse.modules.picking.details.serialNumberLower')
                      : t('warehouse.modules.picking.details.batchNumberLower')}
                  </div>
                  <div className="flex gap-2">
                    {activeLine.itemType === 2 && (
                      <button
                        onClick={() => {
                          setSerialNumberLine(activeLine);
                          setShowSerialNumberModal(true);
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        <span className="hidden sm:inline">{t('warehouse.modules.picking.details.serialNumber')}</span>
                        <span className="sm:hidden">{t('warehouse.modules.picking.details.serialShort')}</span>
                        {activeLine.serialNumbers && activeLine.serialNumbers.length > 0 && (
                          <span className="bg-blue-500 px-1.5 py-0.5 rounded text-xs">
                            {activeLine.serialNumbers.length}/{Math.floor(activeLine.qty)}
                          </span>
                        )}
                      </button>
                    )}
                    {activeLine.itemType === 1 && (
                      <button
                        onClick={() => {
                          setBatchNumberLine(activeLine);
                          setShowBatchNumberModal(true);
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                      >
                        <span className="hidden sm:inline">{t('warehouse.modules.picking.details.batchNumber')}</span>
                        <span className="sm:hidden">{t('warehouse.modules.picking.details.batchShort')}</span>
                        {activeLine.batchNumbers && activeLine.batchNumbers.length > 0 && (
                          <span className="bg-purple-500 px-1.5 py-0.5 rounded text-xs">
                            {activeLine.batchNumbers.length}
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Handlingslinje for multi-select flytting — vises når minst én linje er markert */}
            {moveSelection.size > 0 && (
              <div className="flex items-center justify-between gap-2 mb-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg">
                <span className="text-sm font-medium text-purple-800">
                  {t('warehouse.modules.picking.actions.selectedCount', { '0': moveSelection.size }, `${moveSelection.size} linjer markert`)}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setMoveSelection(new Set())}
                    className="px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg min-h-[40px]"
                  >
                    {t('common.buttons.cancel', {}, 'Avbryt')}
                  </button>
                  <button
                    onClick={handleMoveSelectedToEnd}
                    className="px-3 py-2 text-sm font-semibold text-white bg-purple-600 rounded-lg min-h-[40px]"
                  >
                    {t('warehouse.modules.picking.actions.moveSelectedToEnd', {}, 'Flytt valgte til slutt')}
                  </button>
                </div>
              </div>
            )}

            <PickingLinesList
              lines={pickingLines.map(convertToPickingLine)}
              currentLineIndex={currentLineIndex}
              activeLineIndex={activeLineIndex}
              pallets={palletSummaries}
              editingLocation={editingLocation}
              editingQuantity={editingQuantity}
              newLocation={newLocation}
              newQuantity={newQuantity}
              showPalletInfo={showPalletInfo}
              onLineClick={handleLineSelect}
              onLocationEdit={handleLocationEdit}
              onQuantityEdit={handleQuantityEdit}
              onStartLocationEdit={(lineNo, location) => {
                setEditingLocation(lineNo);
                setNewLocation(location);
              }}
              onStartQuantityEdit={(lineNo) => {
                // Samme modal-vei som aktiv-kortet — inline-boksen kollapser ved re-render
                openSetTotalModal(lineNo);
              }}
              onLocationChange={setNewLocation}
              onQuantityChange={setNewQuantity}
              onSplitLine={handleSplitLine}
              onPickLine={handlePickLine}
              onUndoPicking={handleUndoPicking}
              onShowPalletInfo={setShowPalletInfo}
              onReorder={handleReorder}
              onMoveToEnd={handleMoveToEnd}
              enableDragAndDrop={enableDragAndDrop}
              moveSelectedLineNos={moveSelection}
              onToggleMoveSelect={handleToggleMoveSelect}
              onStructureCount={handleStructureCountRequest}
              onDeletePallet={autoCreatePalletOnDPakScan ? deletePallet : undefined}
              onDeleteAdditionalLine={handleDeleteAdditionalLine}
              onEditAdditionalLine={openEditAdditionalLine}
              largeText={largeText}
              hideSplit={autoCreatePalletOnDPakScan}
              dpakMode={autoCreatePalletOnDPakScan}
            />
          </div>
        </div>
      </div>

      {/* Image Panel */}
      <ImagePanel
        isOpen={showImagePanel && !!activeLine?.image}
        imageUrl={activeLine?.image || ''}
        itemDescription={activeLine?.itemDescription || ''}
        onZoom={() => {
          if (activeLine?.image) {
            setSelectedImage(activeLine.image);
            setShowImageModal(true);
          }
        }}
        onClose={() => setShowImagePanel(false)}
      />

      {/* Modals */}
      <LocationScanModal
        isOpen={showLocationScan}
        expectedLocation={currentLine?.location || ''}
        tempGS1Quantity={tempGS1Quantity}
        locationScan={locationScan}
        onLocationScanChange={setLocationScan}
        onConfirm={handleLocationScanConfirm}
        onOverride={() => setShowLocationOverride(true)}
        onClose={() => {
          setShowLocationScan(false);
          setLocationScan('');
          setTempGS1Quantity(null);
        }}
      />

      <LocationOverrideModal
        isOpen={showLocationOverride}
        expectedLocation={currentLine?.location || ''}
        overrideLocation={overrideLocation}
        companyNo={pickingDetails.header.companyNo}
        warehouseID={currentLine?.warehouseID || ''}
        itemNo={currentLine?.itemNo || ''}
        requiredQuantity={currentLine ? currentLine.qty - currentLine.qtyPicked : 0}
        isShortageLine={currentLine?.binCode === '[X]'}
        isAdvancedWarehouse={isAdvancedWarehouse}
        isStockControlled={currentLine?.stcTransactionsYesNo !== false}
        onLocationChange={setOverrideLocation}
        onConfirm={handleLocationOverride}
        onResolveShortage={handleResolveShortage}
        onClose={() => {
          setShowLocationOverride(false);
          setOverrideLocation('');
        }}
      />

      {activeLine && (
        <LocationStockModal
          isOpen={showLocationStock}
          onClose={() => setShowLocationStock(false)}
          itemNo={activeLine.itemNo || ''}
          itemDescription={activeLine.itemDescription}
          currentBinCode={activeLine.binCode || ''}
          warehouseId={activeLine.warehouseID}
          requiredQuantity={activeLine.qty - activeLine.qtyPicked}
          isStockControlled={activeLine.stcTransactionsYesNo !== false}
          onOverride={handleLocationOverrideFromStock}
        />
      )}

      <ImageModal
        isOpen={showImageModal}
        imageUrl={selectedImage}
        onClose={() => setShowImageModal(false)}
      />

      <SplitModal
        isOpen={showSplitModal}
        line={convertToPickingLineWithCheck(splitLineNo ? pickingLines.find(l => l.pListLineNo === splitLineNo) : undefined)}
        pallets={palletSummaries}
        splitQuantities={splitQuantities}
        onQuantityChange={(palletId, quantity) => setSplitQuantities({
          ...splitQuantities,
          [palletId]: quantity
        })}
        onConfirm={confirmSplit}
        onClose={closeSplitModal}
      />

      <QuantityDistributionModal
        isOpen={showQuantityDistribution}
        line={convertToPickingLineWithCheck(distributionLineNo ? pickingLines.find(l => l.pListLineNo === distributionLineNo) : undefined)}
        pallets={palletSummaries}
        distributionQuantities={distributionQuantities}
        onQuantityChange={(palletId, quantity) => setDistributionQuantities({
          ...distributionQuantities,
          [palletId]: quantity
        })}
        onConfirm={confirmQuantityDistribution}
        onClose={closeDistributionModal}
      />

      <PalletInfoModal
        isOpen={!!showPalletInfo}
        pallet={showPalletInfo ? palletSummaries.find(p => p.sscc === showPalletInfo) || null : null}
        onClose={() => setShowPalletInfo(null)}
      />

      <DeliveryInfoModal
        /* key: remonteres etter lastbærer-registrering (36334239) så Transport-detaljer
           lastes på nytt og viser de nye kollilinjene */
        key={`delivery-info-${deliveryInfoKey}`}
        isOpen={showDeliveryInfoModal}
        pListId={pListId}
        companyNo={pickingDetails?.header.companyNo || ''}
        pListOrders={pListOrders}
        userProfile={userProfile}
        onConfirm={handleDeliveryInfoConfirm}
        onClose={() => setShowDeliveryInfoModal(false)}
        pallets={palletSummaries}
        isBatch={!!pickingDetails?.header.pListIsBatchYesNo}
      />

      {/* Leveringsmåte-varsel ved avslutt plukk (Strømmes): vises OVER leveranse-
          informasjonen (z-[60] + etter i DOM) når valgt leveringsmåte har
          varslingsflagg — må bekreftes med OK før plukket fullføres */}
      <PickCompletionWarningModal
        isOpen={showPickCompletionWarning}
        warnings={pickCompletionWarnings}
        onConfirm={handlePickCompletionWarningConfirm}
        onCancel={handlePickCompletionWarningCancel}
      />

      {/* Pack zone selection modal for "Til Pakking" */}
      <PackZoneSelectionModal
        isOpen={showPackZoneSelection}
        pListId={pListId}
        pListOrders={pListOrders}
        onConfirm={handlePackZoneConfirm}
        onClose={() => setShowPackZoneSelection(false)}
      />

      {/* Package scan modal for batch picking - must scan correct package before quantity entry */}
      <PackageScanModal
        isOpen={showPackageScan}
        onClose={() => {
          setShowPackageScan(false);
          setLineToConfirm(null);
          setQuantityToConfirm(0);
        }}
        onConfirm={handlePackageScanConfirm}
        packageNo={lineToConfirm?.packageNo ?? 0}
        validPackageNos={getValidPackageNos(lineToConfirm)}
        visiblePackageNo={lineToConfirm?.visiblePackageNo}
        itemNo={lineToConfirm?.itemNo}
        customerName={lineToConfirm?.customerName}
      />

      {/* D-pak: antall-fallback når verken etiketten eller varens enhetsfaktor oppgir antall.
          Gjenbruker kvantumsmodalen i 'add'-modus (validerer 1..gjenstående). */}
      <QuantityConfirmModal
        isOpen={dpak.quantityPrompt !== null}
        line={dpak.quantityPrompt?.line ?? null}
        proposedQuantity={dpak.quantityPrompt?.remaining ?? 0}
        mode="add"
        onConfirm={dpak.confirmQuantity}
        onClose={dpak.cancelQuantity}
      />

      <QuantityConfirmModal
        isOpen={showQuantityConfirm}
        line={lineToConfirm}
        proposedQuantity={quantityToConfirm}
        mode={quantityModalMode}
        onConfirm={quantityModalMode === 'setTotal' ? handleSetTotalConfirm : handleQuantityConfirm}
        onReportMissing={isAdvancedWarehouse ? handleReportMissing : undefined}
        onClose={() => {
          setShowQuantityConfirm(false);
          setLineToConfirm(null);
          setQuantityToConfirm(0);
          setQuantityModalMode('add');
          // Resttelling: avbrutt plukk → ingenting er registrert (verken telling eller plukk).
          // Fjern armeringen; telle-gaten re-armerer ved neste plukkforsøk på linjen.
          setArmedCountBack(null);
        }}
      />

      <QuantityDeviationModal
        isOpen={showQuantityDeviation}
        deviations={quantityDeviations}
        onConfirm={handleQuantityDeviationConfirm}
        onCancel={handleQuantityDeviationCancel}
      />

      <SignatureModal
        isOpen={showSignatureModal}
        onConfirm={handleSignatureConfirm}
        onClose={() => setShowSignatureModal(false)}
      />

      {/* TA-forhåndsvalidering (36369121): avvik fra nShift vises FØR booking,
          med valget «Avbryt og rett» / «Send likevel, og rett i nShift» */}
      <TaValidationDialog
        isOpen={showTaValidation}
        issues={taValidationIssues}
        busy={taValidationBusy}
        onCancel={handleTaValidationCancel}
        onProceed={handleTaValidationProceed}
        allowCountryCodeFix
        errorMessage={taValidationError}
      />

      {/* Lastbærer-gate (36334239): valget mellom å avslutte uten bærer og å registrere */}
      <CarrierGateDialog
        isOpen={showCarrierGate}
        busy={carrierBusy}
        onCompleteWithout={handleCompleteWithoutCarrier}
        onRegisterCarriers={handleOpenCarrierDistribution}
        onClose={handleCarrierGateCancel}
      />

      {/* Fordelingsbildet: opprett lastbærere og fordel plukklinjene på dem */}
      <CarrierDistributionModal
        isOpen={showCarrierDistribution}
        pickingLines={pickingLines}
        carriers={palletCarriers}
        saving={carrierBusy}
        onConfirm={handleCarrierDistributionConfirm}
        onClose={handleCarrierDistributionCancel}
      />

      <MandatoryCountingModal
        isOpen={showMandatoryCounting}
        itemNo={mandatoryCountingData?.itemNo || ''}
        itemDescription={mandatoryCountingData?.itemDescription || ''}
        binCode={mandatoryCountingData?.binCode || ''}
        lastCountedDate={mandatoryCountingData?.lastCountedDate}
        daysSinceLastCounted={mandatoryCountingData?.daysSinceLastCounted}
        currentQuantity={mandatoryCountingData?.currentQuantity || 0}
        onSubmit={handleMandatoryCountingSubmit}
        isSubmitting={isSubmittingCounting}
      />

      {/* Resttelling (compass.MandatoryCountingPickFirst): telle-plikt utløst, operatøren har
          bekreftet plukkantall og teller nå det som ligger igjen. Ikke avbrytbar (som
          obligatorisk telling); talt totalsaldo = rest + plukket beregnes i handleRestCountSubmit.
          currentQuantity viser forventet rest = registrert saldo − plukket antall. */}
      <MandatoryCountingModal
        isOpen={restCountPending !== null}
        itemNo={armedCountBack?.itemNo || ''}
        itemDescription={armedCountBack?.itemDescription || ''}
        binCode={armedCountBack?.binCode || ''}
        lastCountedDate={armedCountBack?.lastCountedDate}
        daysSinceLastCounted={armedCountBack?.daysSinceLastCounted}
        currentQuantity={Math.max(0, (armedCountBack?.currentQuantity || 0) - (restCountPending?.confirmedQuantity || 0))}
        onSubmit={handleRestCountSubmit}
        isSubmitting={isSubmittingCounting}
        subtitle={t('warehouse.modules.picking.mandatoryCounting.restSubtitle', {}, 'Plukket er bekreftet — tell det som ligger igjen på lokasjonen')}
        confirmLabel={t('warehouse.modules.picking.mandatoryCounting.restConfirm', {}, 'Registrer telling og fortsett')}
        inputLabel={t('warehouse.modules.picking.mandatoryCounting.restInputLabel', {}, 'Antall som ligger igjen på lokasjonen')}
        inputHint={t('warehouse.modules.picking.mandatoryCounting.restInputHint', {}, 'Tell kun det som ligger igjen — det du nettopp plukket skal ikke telles med')}
        stockLabel={t('warehouse.modules.picking.mandatoryCounting.restExpectedRemaining', {}, 'Forventet rest etter plukk')}
      />

      {/* Strukturplukk: frivillig telle-snarvei ved maks mulig sett-avvik */}
      <MandatoryCountingModal
        isOpen={structureCountData !== null}
        itemNo={structureCountData?.itemNo || ''}
        itemDescription={structureCountData?.itemDescription || ''}
        binCode={structureCountData?.binCode || ''}
        currentQuantity={structureCountData?.currentQuantity || 0}
        onSubmit={handleStructureCountSubmit}
        isSubmitting={isSubmittingStructureCount}
        title={t('warehouse.modules.picking.structure.countTitle', {}, 'Tell lokasjon')}
        subtitle={t('warehouse.modules.picking.structure.countSubtitle', {}, 'Registrer faktisk antall på lokasjonen — dekningen for strukturvaren oppdateres')}
        confirmLabel={t('warehouse.modules.picking.structure.countConfirm', {}, 'Registrer telling')}
        onCancel={() => setStructureCountData(null)}
      />

      <ZeroPointCountingModal
        isOpen={showZeroPointCounting}
        itemNo={zeroPointCountingData?.itemNo || ''}
        itemDescription={zeroPointCountingData?.itemDescription || ''}
        binCode={zeroPointCountingData?.binCode || ''}
        pickedQuantity={zeroPointCountingData?.pickedQuantity || 0}
        expectedRemaining={zeroPointCountingData?.expectedRemaining || 0}
        onConfirmZero={handleZeroPointConfirmZero}
        onSubmitDeviation={handleZeroPointSubmitDeviation}
        isSubmitting={isSubmittingZeroPoint}
      />

      <SettingsModal
        isOpen={showSettingsModal}
        autoShowImage={autoShowImage}
        onAutoShowImageChange={setAutoShowImage}
        enableDragAndDrop={enableDragAndDrop}
        onEnableDragAndDropChange={setEnableDragAndDrop}
        enableRapidScanning={enableRapidScanning}
        onEnableRapidScanningChange={setEnableRapidScanning}
        onClose={() => setShowSettingsModal(false)}
      />

      {/* Picking Print Modal — bruker Reports-tabellen og LayoutJson via printingService */}
      <PickingPrintModal
        isOpen={showPickingPrintModal}
        onClose={() => setShowPickingPrintModal(false)}
        pickingDetails={pickingDetails}
        activeLine={activeLine}
      />

      {/* Serial Number Registration Modal (ItemType=2 - free-text entry) */}
      <SerialNumberModal
        isOpen={showSerialNumberModal}
        line={serialNumberLine}
        pListId={pListId}
        onClose={() => {
          setShowSerialNumberModal(false);
          setSerialNumberLine(null);
        }}
        onSerialNumbersChanged={() => {
          // Refresh picking details to update serial numbers
          loadPickingDetails();
        }}
      />

      {/* Serial Pick Modal (ItemType=6 - select from inventory) */}
      <SerialPickModal
        isOpen={showSerialPickModal}
        line={serialPickLine}
        pListId={pListId}
        onClose={() => {
          setShowSerialPickModal(false);
          setSerialPickLine(null);
        }}
        onSerialPicked={handleSerialPicked}
      />

      {/* Batch Pick Modal (ItemType=4/5 - select batch from inventory) */}
      <BatchPickModal
        isOpen={showBatchPickModal}
        line={batchPickLine}
        pListId={pListId}
        onClose={() => {
          setShowBatchPickModal(false);
          setBatchPickLine(null);
        }}
        onBatchPicked={handleBatchPicked}
      />

      {/* Batch Number Registration Modal */}
      <BatchNumberModal
        isOpen={showBatchNumberModal}
        line={batchNumberLine}
        pListId={pListId}
        onClose={() => {
          setShowBatchNumberModal(false);
          setBatchNumberLine(null);
        }}
        onBatchNumbersChanged={() => {
          // Refresh picking details to update batch numbers
          loadPickingDetails();
        }}
      />

      {/* Pallet Manager */}
      {showPalletManager && (
        <PalletManager
          pallets={palletSummaries}
          activePalletSSCC={activePalletSSCC}
          onCreatePallet={createNewPallet}
          onSetActivePallet={setActivePallet}
          onDeletePallet={deletePallet}
          onClose={() => setShowPalletManager(false)}
          pListId={pListId}
          onReload={loadPallets}
          ssccReports={ssccReports}
          selectedSsccReportId={selectedSsccReportId}
          onSelectSsccReport={setSelectedSsccReportId}
          carriers={palletCarriers}
          selectedCarrierId={palletCarrierId}
          onSelectCarrier={setPalletCarrierId}
          onChangeCarrier={changePalletCarrier}
          unplacedQty={unplacedQty}
          hasPickedLines={hasPickedLines}
          onDistribute={() => setShowPickDistribution(true)}
        />
      )}

      {/* Fordel uplasserte varer under plukk (TW 36396316) — samme modal som ved avslutning */}
      <CarrierDistributionModal
        isOpen={showPickDistribution}
        pickingLines={pickingLines}
        carriers={palletCarriers}
        saving={pickDistributionBusy}
        existingCarriers={existingDistributionCarriers}
        title={t('warehouse.modules.picking.pallets.distributeTitle', {}, 'Fordel uplasserte varer')}
        confirmLabel={t('warehouse.modules.picking.pallets.distributeConfirm', {}, 'Lagre fordeling')}
        onConfirm={handlePickDistributionConfirm}
        onClose={() => setShowPickDistribution(false)}
      />

      {/* Add Line Modal */}
      <AddLineModal
        isOpen={showAddLineModal}
        pListId={pListId}
        onClose={() => setShowAddLineModal(false)}
        onSuccess={async (result) => {
          // TW 36393700: «Legg til vare» kobles mot aktiv pall når en finnes (linja er fullplukket i backend).
          if (activePallet && result?.quantity > 0) {
            try {
              await pickingService.createOrUpdateSSCC(pListId, {
                companyNo: '',
                pListID: pListId,
                orderNo: result.orderNo,
                lineNo: result.lineNo,
                sscc: activePallet.sscc,
                quantity: result.quantity,
                ssccLevel: 1,
                itemBatchNo: '',
                itemBatchExpDate: null,
                stackingWeight: 0,
                grossWeight: 0,
                locationName: result.binCode,
                transitPackagingID: carrierIdForPallet(activePallet.sscc)
              });
            } catch (palletErr) {
              console.warn('Kunne ikke koble tilleggslinje til aktiv pall:', palletErr);
            }
          }
          await loadPickingDetails(); // Refresh picking list to show new line
          await loadPallets();
        }}
      />

      {/* Bottom Status Bar */}
      <StatusBar
        completedLines={completedLines}
        totalLines={pickingLines.filter(l => l.binCode !== '[X]').length}
        pallets={palletSummaries}
        activePalletSSCC={activePalletSSCC}
        priority={pickingDetails.header.priority}
      />
    </div>
  );
};